/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 3 PRO React components

// Billing page components

import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDProgress from "components/MDProgress";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";

import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Icon from "@mui/material/Icon";
import { Icon as MuiIcon } from "@mui/material";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MDDropzone from "components/MDDropzone";
import swal from "sweetalert";
import Autocomplete from "@mui/material/Autocomplete";
import MDInput from "components/MDInput";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { useMaterialUIController } from "context";

import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD, API_CHAT } from "utils";
import { logoutUser } from "actions/AuthAction";
import ChatIcon from "@mui/icons-material/Chat";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import Avatar from "@mui/material/Avatar";

const PRIORITAS_COLOR = {
  Rendah: "#4caf50",
  Sedang: "#ffb300",
  Tinggi: "#f44336",
};

// Validasi tipe file
const isValidFileType = (file) => {
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "image/jpeg",
    "image/jpg",
  ];
  const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".xlsm",
    ".jpg",
    ".jpeg",
  ];
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."));
  return (
    allowedTypes.includes(file.type) ||
    allowedExtensions.includes(fileExtension)
  );
};

// Validasi total ukuran semua file (3 MB = 3 * 1024 * 1024 bytes)
const isValidTotalSize = (files) => {
  const maxTotalSize = 3 * 1024 * 1024; // 3 MB total dalam bytes
  const totalSize = files.reduce((sum, file) => {
    const fileObj = file.file || file;
    return sum + (fileObj.size || 0);
  }, 0);
  return totalSize <= maxTotalSize;
};

// Helper untuk memilih icon dokumen
function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf")
    return (
      <PictureAsPdfIcon sx={{ color: "#f44336", mr: 1 }} fontSize="small" />
    );
  if (["png", "jpg", "jpeg", "gif", "bmp", "svg", "webp"].includes(ext))
    return <ImageIcon sx={{ color: "#1976d2", mr: 1 }} fontSize="small" />;
  if (["doc", "docx"].includes(ext))
    return (
      <DescriptionIcon sx={{ color: "#1565c0", mr: 1 }} fontSize="small" />
    );
  if (["xls", "xlsx"].includes(ext))
    return (
      <DescriptionIcon sx={{ color: "#388e3c", mr: 1 }} fontSize="small" />
    );
  if (["ppt", "pptx"].includes(ext))
    return (
      <DescriptionIcon sx={{ color: "#ff9800", mr: 1 }} fontSize="small" />
    );
  return (
    <InsertDriveFileIcon sx={{ color: "#757575", mr: 1 }} fontSize="small" />
  );
}

// Helper format tanggal
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Helper untuk format datetime
function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Data chart instruksi dibuat & diselesaikan
const chartInstruksi = {
  labels: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "Mei",
    "Jun",
    "Jul",
    "Agu",
    "Sep",
    "Okt",
    "Nov",
    "Des",
  ],
  datasets: [
    {
      chartType: "thin-bar",
      label: "Instruksi Dibuat",
      color: "info",
      data: [2, 3, 4, 5, 2, 1, 3, 4, 2, 3, 2, 1],
    },
    {
      chartType: "gradient-line",
      label: "Instruksi Selesai",
      color: "success",
      data: [1, 2, 2, 3, 1, 2, 2, 3, 1, 2, 1, 2],
    },
  ],
};

function DaftarTugas() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [instruksiOleh, setInstruksiOleh] = useState([]);
  const [openDetail, setOpenDetail] = useState({});
  const [instruksiTerima, setInstruksiTerima] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(""); // 'lanjutkan' | 'selesaikan'
  const [selectedInstruksi, setSelectedInstruksi] = useState(null);
  const [modalValues, setModalValues] = useState({
    catatan: "",
    files: [],
    penerima: [],
    tingkat_perintah: "",
    status_pelaksana: "",
    waktu_diterima: "",
    waktu_selesai: "",
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [penerimaOptions, setPenerimaOptions] = useState([]);
  const [penerima, setPenerima] = useState({ penerima: [] });
  const dropzoneRef = useRef(null);

  const [tingkatPerintah, setTingkatPerintah] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openDeleteInstruksiDialog, setOpenDeleteInstruksiDialog] =
    useState(false);
  const [deleteInstruksiTarget, setDeleteInstruksiTarget] = useState(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [filteredInstruksi, setFilteredInstruksi] = useState([]);
  const [expandedPenerima, setExpandedPenerima] = useState({});
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openPenerimaDialog, setOpenPenerimaDialog] = useState(false);
  const [selectedPenerima, setSelectedPenerima] = useState(null);
  const [selectedInstruksiForDialog, setSelectedInstruksiForDialog] =
    useState(null);
  const [statusUser, setStatusUser] = useState(null);
  const [penerimaFilterType, setPenerimaFilterType] = useState(null); // 'langsung' | 'lanjutan' | null (semua)
  const [openIsiInstruksiDialog, setOpenIsiInstruksiDialog] = useState(false);
  const [selectedIsiInstruksi, setSelectedIsiInstruksi] = useState(null);
  const [openPerihalDialog, setOpenPerihalDialog] = useState(false);
  const [selectedPerihal, setSelectedPerihal] = useState(null);
  const [tahun, setTahun] = useState(null);
  const [bulan, setBulan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editInstruksi, setEditInstruksi] = useState(null);
  const [editFormData, setEditFormData] = useState({
    perihal: "",
    tanggal: "",
    tanggal_selesai: "",
    isi_instruksi: "",
    prioritas: null,
    penerima: [],
    dokumen: [],
  });
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [deleteInstruksiId, setDeleteInstruksiId] = useState(null);
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [selectedChatInstruksi, setSelectedChatInstruksi] = useState(null);
  const [selectedChatPenerima, setSelectedChatPenerima] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const chatEndRef = useRef(null);

  // State untuk dialog respon pemberi instruksi
  const [openResponDialog, setOpenResponDialog] = useState(false);
  const [selectedPenerimaForRespon, setSelectedPenerimaForRespon] =
    useState(null);
  const [selectedRespon, setSelectedRespon] = useState("");
  const [openRatingDialog, setOpenRatingDialog] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [submittingRespon, setSubmittingRespon] = useState(false);

  // Ambil darkMode dari context
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const navigate = useNavigate();

  useEffect(() => {
    // Ambil dinas_id dari localStorage
    let dinas_id = null;
    let user_id = null;
    let status = null;
    try {
      const user = localStorage.getItem("user");
      if (user) {
        const parsed = JSON.parse(user);
        dinas_id = parsed.dinas_id;
        user_id = parsed.uid;
        status = parsed.status;
      } else {
        logoutUser(navigate);
      }
    } catch (e) {}
    if (!dinas_id) return;
    axios
      .get(
        status === "pimpinan"
          ? `${API_ESA_OFFICE}users/pimpinan/list`
          : `${API_ESA_OFFICE}users/dinas/${dinas_id}`
      )
      .then((res) => {
        const options = (res.data || [])
          .filter((data) => data.user_id !== user_id)
          .map((data) => ({
            label: data.nama ? data.nama.split(" ")[0] : "-",
            value: data.user_id,
            avatar: data.uid_RSS
              ? `https://smartdh.rotendaokab.go.id/uploads/${data.uid_RSS}.jpg`
              : undefined,
          }));
        setPenerimaOptions(options);
      })
      .catch((err) => {
        setPenerimaOptions([]);
      });
    // eslint-disable-next-line
  }, []);

  const allOptions = [
    { label: "Pilih Semua", value: "all", avatar: "" },
    ...penerimaOptions,
  ];

  const handleChange = (event, value) => {
    const isSelectAll = value.some((v) => v.value === "all");
    if (isSelectAll) {
      if ((penerima.penerima || []).length === penerimaOptions.length) {
        setPenerima({ penerima: [] });
        setModalValues((prev) => ({ ...prev, penerima: [] }));
      } else {
        setPenerima({ penerima: penerimaOptions });
        setModalValues((prev) => ({ ...prev, penerima: penerimaOptions }));
      }
    } else {
      setPenerima({ penerima: value });
      setModalValues((prev) => ({ ...prev, penerima: value }));
    }
  };

  useEffect(() => {
    // Ambil userId dari localStorage saat mount
    let uid = null;
    try {
      const user = localStorage.getItem("user");
      if (user) {
        const parsed = JSON.parse(user);
        uid = parsed.uid;
      }
    } catch (e) {}
    setUserId(uid);

    // Set tahun dan bulan default ke tahun dan bulan saat ini
    const now = new Date();
    setTahun(now.getFullYear());
    setBulan(now.getMonth() + 1);
  }, []);

  function ambilData() {
    if (!userId || !tahun) return Promise.resolve();

    setLoading(true);
    let url = `${API_ESA_OFFICE}instruksi/oleh/${userId}?tahun=${tahun}`;
    if (bulan && bulan !== "semua" && bulan !== "0") {
      url += `&bulan=${bulan}`;
    }

    return axios
      .get(url)
      .then((response) => {
        // Sort berdasarkan tanggal (terbaru ke lama)
        const sortedData = [...(response.data || [])].sort((a, b) => {
          const dateA = a.tanggal ? new Date(a.tanggal) : new Date(0);
          const dateB = b.tanggal ? new Date(b.tanggal) : new Date(0);
          return dateB - dateA; // Descending: terbaru ke lama
        });
        setInstruksiOleh(sortedData);
        setLoading(false);
        return sortedData;
      })
      .catch((error) => {
        setInstruksiOleh([]);
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!userId || !tahun) return;
    ambilData();
  }, [userId, tahun, bulan]);

  // Refresh data di dialog daftar penerima jika instruksiOleh berubah dan dialog masih terbuka
  useEffect(() => {
    if (
      openPenerimaDialog &&
      selectedInstruksiForDialog &&
      instruksiOleh.length > 0
    ) {
      const updatedInstruksi = instruksiOleh.find(
        (item) => item.instruksi_id === selectedInstruksiForDialog.instruksi_id
      );
      if (updatedInstruksi && updatedInstruksi.rincian_penerima) {
        // Update selectedPenerima dengan data terbaru
        setSelectedPenerima(updatedInstruksi.rincian_penerima);
      }
    }
  }, [instruksiOleh, openPenerimaDialog, selectedInstruksiForDialog]);

  // Handler modal input
  const handleModalInput = (field, value) => {
    setModalValues((prev) => ({ ...prev, [field]: value }));
  };

  // Inject file ke Dropzone saat modalValues.files berubah (hanya jika modal terbuka)
  useEffect(() => {
    if (
      dropzoneRef.current &&
      modalValues.files &&
      Array.isArray(modalValues.files) &&
      modalValues.files.length > 0 &&
      openModal
    ) {
      const dz = dropzoneRef.current.dropzone;
      if (dz && dz.files.length === 0) {
        modalValues.files.forEach((file) => {
          const fileObj = file.file || file;
          // Validasi file sebelum inject
          if (!isValidFileType(fileObj)) {
            console.warn(`File ${fileObj.name} tidak valid dan akan diabaikan`);
            return;
          }
          if (
            !dz.files.find(
              (f) => f.name === fileObj.name && f.size === fileObj.size
            )
          ) {
            dz.emit("addedfile", fileObj);
            dz.emit("complete", fileObj);
          }
        });
      }
    }
  }, [modalValues.files, openModal]);

  // Event handler untuk memvalidasi dan menolak file sebelum ditambahkan ke dropzone
  useEffect(() => {
    const dz = dropzoneRef.current?.dropzone;
    if (!dz) return;

    // Handler untuk memvalidasi file sebelum ditambahkan (menggunakan accept)
    const handleAccept = (file, done) => {
      // Validasi tipe file
      if (!isValidFileType(file)) {
        done("Format file tidak didukung");
        return;
      }

      // Hitung total ukuran file yang sudah ada di dropzone
      const existingFilesSize = dz.files.reduce((sum, f) => {
        return sum + (f.size || 0);
      }, 0);

      // Hitung total ukuran setelah menambahkan file baru
      const totalSize = existingFilesSize + file.size;
      const maxTotalSize = 3 * 1024 * 1024; // 3 MB
      const totalSizeMB = totalSize / (1024 * 1024);

      // Jika total ukuran melebihi batas, tolak file
      if (totalSize > maxTotalSize) {
        done(`Total ukuran melebihi 3 MB (${totalSizeMB.toFixed(2)} MB)`);

        // Tampilkan pesan error
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal 3 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        return;
      }

      // File diterima
      done(null);
    };

    // Handler untuk memvalidasi multiple files (menggunakan addedfiles sebagai fallback)
    const handleAddedFiles = (files) => {
      // Gunakan setTimeout untuk memastikan file sudah ditambahkan ke dz.files
      setTimeout(() => {
        // Hitung total ukuran file yang sekarang ada di dropzone
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 3 * 1024 * 1024; // 3 MB
        const totalSizeMB = totalSize / (1024 * 1024);

        // Jika total ukuran melebihi batas, hapus file yang baru ditambahkan
        if (totalSize > maxTotalSize) {
          // Hapus file yang baru ditambahkan (file yang ada di parameter files)
          files.forEach((file) => {
            // Validasi tipe file terlebih dahulu
            if (!isValidFileType(file)) {
              // File dengan tipe tidak valid sudah ditolak oleh dropzone sendiri
              return;
            }
            // Hapus file yang menyebabkan total melebihi batas
            if (dz.files.includes(file)) {
              dz.emit("error", file, `Total ukuran melebihi 3 MB`);
              dz.removeFile(file);
            }
          });

          // Tampilkan pesan error
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 3 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        }
      }, 0);
    };

    // Tambahkan event listener untuk accept (validasi sebelum file ditambahkan)
    dz.on("accept", handleAccept);

    // Tambahkan event listener untuk addedfiles sebagai fallback
    dz.on("addedfiles", handleAddedFiles);

    // Cleanup: hapus event listener saat unmount
    return () => {
      if (dz) {
        dz.off("accept", handleAccept);
        dz.off("addedfiles", handleAddedFiles);
      }
    };
  }, [openModal]);

  // Handler Dropzone
  const handleDropzoneChange = (files) => {
    // Filter file yang valid berdasarkan tipe (sebagai double check)
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const fileObj = file.file || file;
      if (!isValidFileType(fileObj)) {
        invalidFiles.push({
          name: fileObj.name,
          reason: "Format file tidak didukung",
        });
      } else {
        validFiles.push(file);
      }
    });

    // Tampilkan pesan error jika ada file yang tidak valid (seharusnya tidak terjadi)
    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles
        .map((f) => `• ${f.name}: ${f.reason}`)
        .join("\n");
      swal({
        title: "File Tidak Valid",
        text: `File berikut tidak dapat diupload:\n\n${errorMessages}\n\nFormat yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG`,
        icon: "error",
        button: "OK",
      });
    }

    // Update dengan file yang valid (file yang melebihi total ukuran sudah ditolak di event handler)
    if (invalidFiles.length === 0) {
      handleModalInput("files", validFiles);
    }
  };

  // Handler submit modal (lanjutkan/selesaikan)
  const handleSubmitModal = async () => {
    if (!selectedInstruksi) return;
    setLoadingModal(true);

    // Validasi total ukuran file sebelum submit
    if (modalValues.files && modalValues.files.length > 0) {
      const totalSize = modalValues.files.reduce((sum, file) => {
        const fileObj = file.file || file;
        return sum + (fileObj.size || 0);
      }, 0);
      const totalSizeMB = totalSize / (1024 * 1024);
      const maxSizeMB = 3;

      if (totalSizeMB > maxSizeMB) {
        swal({
          title: "Ukuran File Melebihi Batas",
          text: `Total ukuran semua file (${totalSizeMB.toFixed(
            2
          )} MB) melebihi batas maksimal ${maxSizeMB} MB.\n\nSilakan hapus beberapa file atau pilih file yang lebih kecil.`,
          icon: "error",
          button: "OK",
        });
        setLoadingModal(false);
        return;
      }
    }

    const missingFields = [];
    if (!modalValues.catatan) missingFields.push("Catatan");
    if (!modalValues.status_pelaksana) missingFields.push("Status Pelaksana");

    if (!modalValues.waktu_selesai) missingFields.push("Waktu Selesai");
    if (
      modalType === "lanjutkan" &&
      (!penerima.penerima || penerima.penerima.length === 0)
    )
      missingFields.push("Penerima");
    if (modalType === "lanjutkan" && !modalValues.waktu_diterima)
      missingFields.push("Waktu Diterima");

    if (missingFields.length > 0) {
      swal(
        "Gagal",
        `Harap isi semua nilai: ${missingFields.join(", ")}`,
        "error"
      );
      setLoadingModal(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("instruksi_tindak_lanjut", modalValues.catatan);
      formData.append("status_pelaksana", modalValues.status_pelaksana);

      formData.append("waktu_selesai", modalValues.waktu_selesai);
      formData.append("catatan_selesai", modalValues.catatan);
      if (modalValues.files && modalValues.files.length > 0) {
        modalValues.files.forEach((f) =>
          formData.append("file_tindak_lanjut", f.file || f)
        );
      }
      let url = "";
      let method = "post";
      if (modalType === "lanjutkan") {
        // Lanjutkan instruksi (POST)
        formData.append("tingkat_perintah", modalValues.tingkat_perintah);
        formData.append("asal_perintah_dari_id", userId);
        const penerimaList = penerima.penerima.map((p) => ({
          penerima_id: p.value,
          tingkat_perintah: selectedInstruksi.tingkat + 1,
          status_pelaksana: modalValues.status_pelaksana,
          waktu_diterima: modalValues.waktu_diterima,
          waktu_selesai: modalValues.waktu_selesai,
          instruksi_tindak_lanjut: modalValues.catatan,
        }));
        formData.append("penerima", JSON.stringify(penerimaList));
        url = `${API_ESA_OFFICE}instruksi/${selectedInstruksi.instruksi_id}/lanjutkan`;
        method = "post";
      } else {
        // Selesaikan instruksi (PUT)
        url = `${API_ESA_OFFICE}instruksi/selesaikan/${selectedInstruksi.instruksi_id}/${userId}`;
        method = "put";
      }
      await axios({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      swal(
        "Sukses",
        `Instruksi berhasil di${
          modalType === "lanjutkan" ? "lanjutkan" : "selesaikan"
        }!`,
        "success"
      );
      ambilData();
      handleCloseModal();
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal mengirim data!",
        "error"
      );
    }
    setLoadingModal(false);
  };

  // Tambahkan fungsi untuk reset modal dan penerima
  const handleCloseModal = () => {
    setOpenModal(false);
    setPenerima({ penerima: [] });
    setTingkatPerintah("");
    setModalValues({
      catatan: "",
      files: [],
      penerima: [],
      tingkat_perintah: "",
      status_pelaksana: "",
      waktu_diterima: "",
      waktu_selesai: "",
    });
  };

  const handleDeleteChild = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(
        `${API_ESA_OFFICE}instruksi/hapus/${deleteTarget.instruksi_id}/${deleteTarget.asal_perintah_dari_id}/${deleteTarget.penerima_id}/`
      );
      swal("Sukses", "Instruksi-penerima berhasil dihapus.", "success");
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
      ambilData();
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal menghapus data!",
        "error"
      );
      setOpenDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleDeleteInstruksi = async () => {
    if (!deleteInstruksiTarget) return;
    try {
      await axios.delete(
        `${API_ESA_OFFICE}instruksi/hapus-utama/${deleteInstruksiTarget.instruksi_id}`
      );
      swal("Sukses", "Instruksi berhasil dihapus.", "success");
      setOpenDeleteInstruksiDialog(false);
      setDeleteInstruksiTarget(null);
      ambilData();
    } catch (err) {
      swal(
        "Gagal",
        err?.response?.data?.error || "Gagal menghapus data!",
        "error"
      );
      setOpenDeleteInstruksiDialog(false);
      setDeleteInstruksiTarget(null);
    }
  };

  // Fungsi helper untuk cek apakah instruksi sudah selesai
  const isInstruksiSelesai = (item, pengirimId) => {
    if (!item.rincian_penerima || !Array.isArray(item.rincian_penerima)) {
      return false;
    }

    const penerimaLangsung = item.rincian_penerima.filter(
      (p) => p.asal_perintah_dari_id === pengirimId
    );

    if (penerimaLangsung.length === 0) {
      return false;
    }

    const selesaiCount = penerimaLangsung.filter(
      (p) => (p.status || "").toLowerCase() === "selesai"
    ).length;

    return (
      selesaiCount === penerimaLangsung.length && penerimaLangsung.length > 0
    );
  };

  // Handler untuk membuka dialog edit
  const handleOpenEditDialog = (item) => {
    setEditInstruksi(item);

    // Parse dokumen jika berupa JSON string
    let dokumenArr = [];
    if (item.dokumen) {
      if (Array.isArray(item.dokumen)) {
        dokumenArr = item.dokumen;
      } else if (
        typeof item.dokumen === "string" &&
        item.dokumen.trim().startsWith("[")
      ) {
        try {
          dokumenArr = JSON.parse(item.dokumen);
        } catch (e) {
          dokumenArr = [];
        }
      }
    }

    // Ambil penerima langsung saja
    const penerimaLangsung = Array.isArray(item.rincian_penerima)
      ? item.rincian_penerima.filter((p) => p.asal_perintah_dari_id === userId)
      : [];

    setEditFormData({
      perihal: item.perihal || "",
      tanggal: item.tanggal
        ? new Date(item.tanggal).toISOString().slice(0, 16)
        : "",
      tanggal_selesai: item.tanggal_selesai
        ? new Date(item.tanggal_selesai).toISOString().slice(0, 16)
        : "",
      isi_instruksi: item.isi_instruksi || "",
      prioritas: item.prioritas || null,
      penerima: penerimaLangsung.map((p) => ({
        penerima_id: p.user_id || p.penerima_id,
        asal_perintah_dari_id: userId,
        tingkat_perintah: p.tingkat || "1",
      })),
      dokumen: dokumenArr,
    });
    setOpenEditDialog(true);
  };

  // Handler untuk menutup dialog edit
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditInstruksi(null);
    setEditFormData({
      perihal: "",
      tanggal: "",
      tanggal_selesai: "",
      isi_instruksi: "",
      prioritas: null,
      penerima: [],
      dokumen: [],
    });
  };

  // Handler untuk submit edit
  const handleSubmitEdit = async () => {
    if (!editInstruksi) return;

    try {
      setLoadingModal(true);
      const formData = new FormData();
      formData.append("pengirim_id", userId);
      formData.append("tanggal", editFormData.tanggal);
      formData.append("tanggal_selesai", editFormData.tanggal_selesai || "");
      formData.append("isi_instruksi", editFormData.isi_instruksi);
      formData.append("prioritas", editFormData.prioritas || "");
      formData.append("status", "Terkirim");
      formData.append("perihal", editFormData.perihal);
      formData.append("penerima", JSON.stringify(editFormData.penerima));

      // Tambahkan dokumen lama jika ada
      if (editFormData.dokumen && editFormData.dokumen.length > 0) {
        const dokumenLama = editFormData.dokumen.filter(
          (d) => typeof d === "string"
        );
        if (dokumenLama.length > 0) {
          formData.append("dokumen", JSON.stringify(dokumenLama));
        }
      }

      // Tambahkan file baru jika ada
      if (editFormData.dokumen && editFormData.dokumen.length > 0) {
        editFormData.dokumen.forEach((file) => {
          if (file instanceof File) {
            formData.append("dokumen", file);
          }
        });
      }

      await axios.put(
        `${API_ESA_OFFICE}instruksi/edit/${editInstruksi.instruksi_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      swal("Berhasil", "Instruksi berhasil diupdate", "success");
      handleCloseEditDialog();
      ambilData();
    } catch (error) {
      swal(
        "Gagal",
        error?.response?.data?.error || "Gagal mengupdate instruksi",
        "error"
      );
    } finally {
      setLoadingModal(false);
    }
  };

  // Handler untuk membuka dialog konfirmasi delete
  const handleOpenDeleteConfirm = (item) => {
    setDeleteInstruksiId(item.instruksi_id);
    setOpenDeleteConfirmDialog(true);
  };

  // Handler untuk menutup dialog konfirmasi delete
  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirmDialog(false);
    setDeleteInstruksiId(null);
  };

  // Handler untuk konfirmasi delete
  const handleConfirmDelete = async () => {
    if (!deleteInstruksiId) return;

    try {
      setLoadingModal(true);
      await axios.delete(
        `${API_ESA_OFFICE}instruksi/hapus-utama/${deleteInstruksiId}`
      );
      swal("Berhasil", "Instruksi berhasil dihapus", "success");
      handleCloseDeleteConfirm();
      ambilData();
    } catch (error) {
      swal(
        "Gagal",
        error?.response?.data?.error || "Gagal menghapus instruksi",
        "error"
      );
    } finally {
      setLoadingModal(false);
    }
  };

  // Handler untuk membuka dialog status
  const handleOpenStatusDialog = (status) => {
    setSelectedStatus(status);

    const today = new Date();
    let filtered = [];

    switch (status) {
      case "selesai":
        filtered = instruksiOleh.filter((item) => {
          if (!Array.isArray(item.rincian_penerima)) return false;
          const penerimaLangsung = item.rincian_penerima.filter(
            (p) => p.asal_perintah_dari_id === userId
          );
          if (penerimaLangsung.length === 0) return false;
          const totalPenerima = penerimaLangsung.length;
          const selesaiCount = penerimaLangsung.filter(
            (p) => (p.status || "").toLowerCase() === "selesai"
          ).length;
          return selesaiCount === totalPenerima && totalPenerima > 0;
        });
        break;
      case "dalamproses":
        filtered = instruksiOleh.filter((item) => {
          if (!Array.isArray(item.rincian_penerima)) return false;
          const penerimaLangsung = item.rincian_penerima.filter(
            (p) => p.asal_perintah_dari_id === userId
          );
          if (penerimaLangsung.length === 0) return false;
          const totalPenerima = penerimaLangsung.length;
          const selesaiCount = penerimaLangsung.filter(
            (p) => (p.status || "").toLowerCase() === "selesai"
          ).length;
          return selesaiCount > 0 && selesaiCount < totalPenerima;
        });
        break;
      case "belumdimulai":
        filtered = instruksiOleh.filter((item) => {
          if (!Array.isArray(item.rincian_penerima)) return false;
          const penerimaLangsung = item.rincian_penerima.filter(
            (p) => p.asal_perintah_dari_id === userId
          );
          if (penerimaLangsung.length === 0) return false;
          const selesaiCount = penerimaLangsung.filter(
            (p) => (p.status || "").toLowerCase() === "selesai"
          ).length;
          return selesaiCount === 0;
        });
        break;
      case "lewatdeadline":
        filtered = instruksiOleh.filter((item) => {
          if (!item.tanggal_selesai) return false;
          const tglSelesai = new Date(item.tanggal_selesai);
          const diffTime =
            tglSelesai.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return diffDays < 0;
        });
        break;
      default:
        filtered = [];
    }

    // Sort berdasarkan tanggal (terbaru ke lama)
    const sortedFiltered = filtered.sort((a, b) => {
      const dateA = a.tanggal ? new Date(a.tanggal) : new Date(0);
      const dateB = b.tanggal ? new Date(b.tanggal) : new Date(0);
      return dateB - dateA; // Descending: terbaru ke lama
    });

    setFilteredInstruksi(sortedFiltered);
    setOpenStatusDialog(true);
  };

  // Handler untuk menutup dialog status
  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setSelectedStatus("");
    setFilteredInstruksi([]);
    setExpandedPenerima({});
  };

  // Handler untuk toggle expand penerima
  const handleTogglePenerima = (penerimaId) => {
    setExpandedPenerima((prev) => ({
      ...prev,
      [penerimaId]: !prev[penerimaId],
    }));
  };

  // Handler untuk membuka file
  const handleOpenFile = (file) => {
    const filename = file.split("/").pop();
    const ext = filename.split(".").pop().toLowerCase();

    if (ext === "pdf") {
      // Buka PDF langsung di tab baru
      window.open(`${API_ESA_OFFICE_UPLOAD}${file}`, "_blank");
    } else {
      // Tampilkan dialog download untuk file non-PDF
      setSelectedFile({
        name: filename,
        url: `${API_ESA_OFFICE_UPLOAD}${file}`,
        type: ext,
      });
      setOpenFileDialog(true);
    }
  };

  // Handler untuk download file
  const handleDownloadFile = () => {
    if (selectedFile) {
      const link = document.createElement("a");
      link.href = selectedFile.url;
      link.download = selectedFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setOpenFileDialog(false);
      setSelectedFile(null);
    }
  };

  // Helper functions untuk render kolom tabel
  // Hanya menghitung penerima langsung (asal_perintah_dari_id === userId)
  const renderProgress = (rincianPenerima, pengirimId) => {
    if (!Array.isArray(rincianPenerima) || rincianPenerima.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          Belum ada penerima
        </MDTypography>
      );
    }
    // Filter hanya penerima langsung (asal_perintah_dari_id === pengirimId)
    const penerimaLangsung = rincianPenerima.filter(
      (p) => p.asal_perintah_dari_id === pengirimId
    );

    if (penerimaLangsung.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          Tidak ada penerima langsung
        </MDTypography>
      );
    }

    const totalPenerima = penerimaLangsung.length;
    const selesaiCount = penerimaLangsung.filter(
      (p) => (p.status || "").toLowerCase() === "selesai"
    ).length;
    const progress =
      totalPenerima > 0 ? Math.round((selesaiCount / totalPenerima) * 100) : 0;

    return (
      <MDBox>
        <MDProgress value={progress} color="info" variant="gradient" />
        <MDTypography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          {selesaiCount}/{totalPenerima} ({progress}%)
        </MDTypography>
      </MDBox>
    );
  };

  const handleOpenPenerimaDialog = (
    penerima,
    instruksiData,
    filterType = null
  ) => {
    setSelectedPenerima(penerima);
    setSelectedInstruksiForDialog(instruksiData);
    setPenerimaFilterType(filterType); // 'langsung' | 'lanjutan' | null (semua)
    setOpenPenerimaDialog(true);
  };

  const handleClosePenerimaDialog = () => {
    setOpenPenerimaDialog(false);
    setSelectedPenerima(null);
    setSelectedInstruksiForDialog(null);
    setPenerimaFilterType(null);
  };

  // Handler untuk dialog isi instruksi
  const handleOpenIsiInstruksiDialog = (isiInstruksi) => {
    setSelectedIsiInstruksi(isiInstruksi);
    setOpenIsiInstruksiDialog(true);
  };

  const handleCloseIsiInstruksiDialog = () => {
    setOpenIsiInstruksiDialog(false);
    setSelectedIsiInstruksi(null);
  };

  // Handler untuk dialog perihal
  const handleOpenPerihalDialog = (perihal) => {
    setSelectedPerihal(perihal);
    setOpenPerihalDialog(true);
  };

  const handleClosePerihalDialog = () => {
    setOpenPerihalDialog(false);
    setSelectedPerihal(null);
  };

  // Handler untuk dialog respon pemberi instruksi
  const handleOpenResponDialog = (penerima) => {
    setSelectedPenerimaForRespon(penerima);
    setSelectedRespon(penerima.respon_pemberi_instruksi || "");
    // Set rating jika sudah ada dan respon adalah Terima
    if (penerima.respon_pemberi_instruksi === "Terima" && penerima.nilai) {
      setSelectedRating(penerima.nilai);
    } else {
      setSelectedRating(0);
    }
    setOpenResponDialog(true);
  };

  const handleCloseResponDialog = () => {
    setOpenResponDialog(false);
    setSelectedPenerimaForRespon(null);
    setSelectedRespon("");
    setSelectedRating(0);
  };

  // Handler untuk memilih respon
  const handleSelectRespon = (respon) => {
    setSelectedRespon(respon);
    if (respon === "Terima") {
      // Jika sedang edit dan sudah ada rating, pertahankan rating
      // Jika belum ada rating, reset ke 0
      if (
        !selectedPenerimaForRespon?.respon_pemberi_instruksi ||
        selectedPenerimaForRespon?.respon_pemberi_instruksi !== "Terima" ||
        !selectedPenerimaForRespon?.nilai
      ) {
        setSelectedRating(0);
      }
      // Tutup dialog respon dan buka dialog rating
      setOpenResponDialog(false);
      setOpenRatingDialog(true);
    } else {
      // Jika memilih Perbaiki atau Tolak, reset rating
      setSelectedRating(0);
    }
  };

  // Handler untuk submit respon
  const handleSubmitRespon = async () => {
    if (!selectedPenerimaForRespon || !selectedPenerimaForRespon.id) {
      swal({
        title: "Error",
        text: "Data penerima tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    if (!selectedRespon) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih respon terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    // Jika Terima, harus ada rating
    if (selectedRespon === "Terima" && selectedRating === 0) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih penilaian terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    setSubmittingRespon(true);
    try {
      const payload = {
        respon_pemberi_instruksi: selectedRespon,
        nilai: selectedRespon === "Terima" ? selectedRating : null,
      };

      await axios.put(
        `${API_ESA_OFFICE}instruksi/respon/${selectedPenerimaForRespon.id}`,
        payload
      );

      swal({
        title: "Berhasil!",
        text:
          selectedRespon === "Terima"
            ? "Respon 'Terima' dan penilaian berhasil disimpan"
            : `Respon '${selectedRespon}' berhasil disimpan. Status diubah menjadi Proses.`,
        icon: "success",
        button: "OK",
      });

      // Simpan instruksi_id untuk refresh dialog
      const instruksiIdForRefresh = selectedInstruksiForDialog?.instruksi_id;
      const isDialogOpen = openPenerimaDialog;

      // Refresh data utama dan update dialog
      ambilData()
        .then((updatedData) => {
          // Update data di dialog jika dialog masih terbuka
          if (isDialogOpen && instruksiIdForRefresh && updatedData) {
            // Cari instruksi terbaru dari data yang baru di-refresh
            const updatedInstruksi = updatedData.find(
              (item) => item.instruksi_id === instruksiIdForRefresh
            );
            if (updatedInstruksi && updatedInstruksi.rincian_penerima) {
              setSelectedPenerima(updatedInstruksi.rincian_penerima);
            }
          }
        })
        .catch((error) => {
          console.error("Error refreshing data:", error);
        });

      handleCloseResponDialog();
      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
    } catch (error) {
      console.error("Error submitting respon:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal mengirimkan respon",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingRespon(false);
    }
  };

  // Handler untuk rating click
  const handleRatingClick = (rating) => {
    setSelectedRating(rating);
  };

  // Handler untuk submit rating (dari dialog rating)
  const handleSubmitRating = async () => {
    if (selectedRating === 0) {
      swal({
        title: "Perhatian",
        text: "Silakan pilih penilaian terlebih dahulu",
        icon: "warning",
        button: "OK",
      });
      return;
    }

    if (!selectedPenerimaForRespon || !selectedPenerimaForRespon.id) {
      swal({
        title: "Error",
        text: "Data penerima tidak valid",
        icon: "error",
        button: "OK",
      });
      return;
    }

    setSubmittingRespon(true);
    try {
      const payload = {
        respon_pemberi_instruksi: "Terima",
        nilai: selectedRating,
      };

      await axios.put(
        `${API_ESA_OFFICE}instruksi/respon/${selectedPenerimaForRespon.id}`,
        payload
      );

      swal({
        title: "Berhasil!",
        text: "Respon 'Terima' dan penilaian berhasil disimpan",
        icon: "success",
        button: "OK",
      });

      // Simpan instruksi_id untuk refresh dialog
      const instruksiIdForRefresh = selectedInstruksiForDialog?.instruksi_id;
      const isDialogOpen = openPenerimaDialog;

      // Refresh data utama dan update dialog
      ambilData()
        .then((updatedData) => {
          // Update data di dialog jika dialog masih terbuka
          if (isDialogOpen && instruksiIdForRefresh && updatedData) {
            // Cari instruksi terbaru dari data yang baru di-refresh
            const updatedInstruksi = updatedData.find(
              (item) => item.instruksi_id === instruksiIdForRefresh
            );
            if (updatedInstruksi && updatedInstruksi.rincian_penerima) {
              setSelectedPenerima(updatedInstruksi.rincian_penerima);
            }
          }
        })
        .catch((error) => {
          console.error("Error refreshing data:", error);
        });

      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
      setSelectedPenerimaForRespon(null);
    } catch (error) {
      console.error("Error submitting respon:", error);
      swal({
        title: "Error",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Gagal mengirimkan respon",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSubmittingRespon(false);
    }
  };

  // Handler untuk cancel rating dialog
  const handleCancelRating = async () => {
    const cancelResult = await swal({
      title: "Konfirmasi",
      text: "Apakah Anda yakin ingin membatalkan? Penilaian wajib diisi untuk respon 'Terima'.",
      icon: "warning",
      buttons: ["Tidak", "Ya, Batalkan"],
      dangerMode: true,
    });

    if (cancelResult) {
      setOpenRatingDialog(false);
      setSelectedRating(0);
      setSelectedRespon("");
      setSelectedPenerimaForRespon(null);
    }
  };

  // Helper function untuk mendapatkan fresh token
  const getFreshToken = async (user) => {
    if (!user?.uid_RSS) {
      throw new Error("User uid_RSS tidak ditemukan");
    }

    try {
      const tokenResponse = await axios.get(
        `${API_ESA_OFFICE}users/uid/${user.uid_RSS}`,
        {
          withCredentials: true, // Kirim cookie untuk refresh token
        }
      );

      if (tokenResponse.data?.accessToken) {
        const token = tokenResponse.data.accessToken;
        // Simpan token ke localStorage
        const updatedUser = { ...user, accessToken: token };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        return token;
      }
      throw new Error("Access token tidak ditemukan di response");
    } catch (error) {
      console.error("Error getting fresh token:", error);
      
      // Handle error khusus untuk status_EO tidak aktif
      if (error.response && error.response.status === 403) {
        const errorData = error.response.data;
        const errorMessage = errorData.reason || errorData.message || "Akun Anda tidak aktif.";
        
        // Clear localStorage dan logout user
        localStorage.removeItem("user");
        localStorage.removeItem("daftarHadir");
        
        swal({
          title: "Akses Ditolak",
          text: errorMessage,
          icon: "error",
          button: "OK",
        }).then(() => {
          window.location.href = "/authentication/sign-in/cover";
        });
        
        throw new Error(errorMessage);
      }
      
      throw error;
    }
  };

  // Fungsi untuk fetch chat history
  const fetchChatHistory = async (instruksiItem, token) => {
    const instruksiId = instruksiItem.instruksi_id;

    // Fetch chat history
    let response;
    try {
      response = await axios.get(`${API_CHAT}instruksi/${instruksiId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
    } catch (error) {
      // Jika 403, coba refresh token sekali lagi
      if (error.response?.status === 403) {
        console.log("🔄 Token mungkin expired, refreshing...");
        try {
          const user = JSON.parse(localStorage.getItem("user")) || {};
          token = await getFreshToken(user);
          response = await axios.get(`${API_CHAT}instruksi/${instruksiId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          });
        } catch (retryError) {
          console.error(
            "❌ Chat API Error after retry:",
            retryError.response?.data || retryError.message
          );
          throw retryError;
        }
      } else {
        throw error;
      }
    }

    // Set chat messages
    if (response && response.data && response.data.success) {
      const messages = response.data.data || [];
      setChatMessages(messages);

      // Auto mark all messages as read
      try {
        const user = JSON.parse(localStorage.getItem("user")) || {};
        const currentUserId =
          userId || user.uid_RSS || user.user_id || user.uid;
        const markReadResponse = await axios.put(
          `${API_CHAT}instruksi/${instruksiId}/read-all`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        if (markReadResponse.data.success) {
          console.log("✅ All messages marked as read");
          setChatMessages((prev) =>
            prev.map((msg) => {
              if (!msg.waktu_baca && msg.penerima_id === currentUserId) {
                return { ...msg, waktu_baca: new Date().toISOString() };
              }
              return msg;
            })
          );
        }
      } catch (markReadError) {
        console.warn("⚠️ Failed to mark messages as read:", markReadError);
      }
    }

    return response;
  };

  // Handler untuk refresh chat messages
  const handleRefreshChat = async () => {
    if (!selectedChatInstruksi || refreshingChat) {
      return;
    }

    setRefreshingChat(true);
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setRefreshingChat(false);
        return;
      }

      const token = await getFreshToken(user);
      await fetchChatHistory(selectedChatInstruksi, token);

      // Scroll to bottom after refresh
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    } catch (error) {
      console.error("Error refreshing chat:", error);
      swal("Error", "Gagal refresh chat. Silakan coba lagi.", "error");
    } finally {
      setRefreshingChat(false);
    }
  };

  // Handler untuk membuka chat
  const handleOpenChat = async (instruksiItem, penerima) => {
    setSelectedChatInstruksi(instruksiItem);
    setSelectedChatPenerima(penerima);
    setOpenChatDialog(true);
    setLoadingChat(true);
    setChatMessages([]);
    setNewMessage(""); // Reset input message

    try {
      // Ambil user dari localStorage
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Selalu dapatkan fresh token untuk memastikan tidak expired
      let token;
      try {
        token = await getFreshToken(user);
        console.log("✅ Fresh token obtained");
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setLoadingChat(false);
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setLoadingChat(false);
        return;
      }

      // Fetch chat history
      try {
        await fetchChatHistory(instruksiItem, token);
        // Scroll to bottom
        setTimeout(() => {
          if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      } catch (error) {
        console.error("Error loading chat:", error);
        if (error.response?.status >= 400) {
          swal(
            "Error",
            error.response?.data?.error || "Gagal memuat chat",
            "error"
          );
        }
        setChatMessages([]);
      }
    } catch (error) {
      console.error("Error loading chat:", error);
      swal("Error", "Gagal memuat chat. Silakan coba lagi.", "error");
    } finally {
      setLoadingChat(false);
    }
  };

  // Handler untuk menutup chat
  const handleCloseChat = () => {
    setOpenChatDialog(false);
    setSelectedChatInstruksi(null);
    setSelectedChatPenerima(null);
    setChatMessages([]);
    setNewMessage("");
  };

  // Handler untuk mengirim pesan
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatInstruksi || !selectedChatPenerima) {
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage(""); // Clear input immediately

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};

      if (!user.uid_RSS) {
        swal("Error", "Data user tidak lengkap. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      // Dapatkan fresh token
      let token;
      try {
        token = await getFreshToken(user);
      } catch (tokenError) {
        console.error("Error getting fresh token:", tokenError);
        swal(
          "Error",
          "Gagal mendapatkan token akses. Silakan login ulang.",
          "error"
        );
        setNewMessage(messageText); // Restore message on error
        return;
      }

      if (!token) {
        swal("Error", "Token tidak ditemukan. Silakan login ulang.", "error");
        setNewMessage(messageText); // Restore message on error
        return;
      }

      // Jangan kirim instruksi_penerima_id agar semua pesan masuk ke room yang sama
      // Room name akan menjadi instruksi_${instruksi_id} tanpa instruksi_penerima_id
      // Pastikan instruksi_id konsisten (number)
      const instruksiIdRaw = selectedChatInstruksi.instruksi_id;
      const instruksiId = parseInt(instruksiIdRaw);
      const roomName = `instruksi_${instruksiId}`;

      console.log("📤 Sending message from daftar-tugas:", {
        instruksi_id_raw: instruksiIdRaw,
        instruksi_id: instruksiId,
        room_name: roomName,
        penerima_id:
          selectedChatPenerima.user_id || selectedChatPenerima.penerima_id,
      });

      const messageData = {
        instruksi_id: instruksiId, // Pastikan sebagai number
        // Tidak kirim instruksi_penerima_id agar semua user di instruksi yang sama menerima pesan
        instruksi_penerima_id: null,
        penerima_id:
          selectedChatPenerima.user_id || selectedChatPenerima.penerima_id,
        pesan: messageText,
      };

      // Optimistic update - tambahkan pesan langsung ke state
      const currentUser = JSON.parse(localStorage.getItem("user")) || {};
      const currentUserId = userId || currentUser.uid || currentUser.user_id;
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const tempMessage = {
        id_chat: tempId,
        pesan: messageText,
        pengirim_id: currentUserId,
        pengirim_nama: currentUser.nama || user.nama || "Anda",
        pengirim_profil: currentUser.uid_RSS || user.uid_RSS,
        waktu_kirim: new Date().toISOString(),
        isSending: true, // Flag untuk menandai pesan sedang dikirim
      };

      // Tambahkan pesan langsung ke state (optimistic update)
      setChatMessages((prev) => {
        // Pastikan tidak ada duplikasi
        const exists = prev.some((msg) => msg.id_chat === tempId);
        if (exists) return prev;
        return [...prev, tempMessage];
      });

      // Scroll ke bawah setelah menambahkan pesan
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);

      // Kirim via REST API
      let response;
      try {
        response = await axios.post(`${API_CHAT}instruksi`, messageData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        if (response.data && response.data.success) {
          // Hapus pesan temporary
          setChatMessages((prev) =>
            prev.filter((msg) => msg.id_chat !== tempId)
          );

          // Refresh chat untuk mendapatkan pesan terbaru
          await fetchChatHistory(selectedChatInstruksi, token);

          // Scroll to bottom
          setTimeout(() => {
            if (chatEndRef.current) {
              chatEndRef.current.scrollIntoView({ behavior: "smooth" });
            }
          }, 150);
        }
      } catch (error) {
        console.error("Error sending message:", error);
        // Hapus pesan temporary jika error
        setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== tempId));
        setNewMessage(messageText); // Restore message
        swal(
          "Error",
          error.response?.data?.error || "Gagal mengirim pesan",
          "error"
        );
        return;
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Hapus pesan temporary jika error
      setChatMessages((prev) =>
        prev.filter((msg) => !msg.isSending || msg.pesan !== messageText)
      );
      setNewMessage(messageText); // Restore message
      swal(
        "Error",
        error.response?.data?.error || "Gagal mengirim pesan",
        "error"
      );
    }
  };

  // Handler untuk delete message
  const handleDeleteMessage = async (chatId) => {
    if (!chatId) return;

    const confirmed = await swal({
      title: "Hapus Pesan?",
      text: "Pesan yang dihapus tidak dapat dikembalikan.",
      icon: "warning",
      buttons: {
        cancel: {
          text: "Batal",
          value: false,
          visible: true,
        },
        confirm: {
          text: "Hapus",
          value: true,
          visible: true,
        },
      },
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const token = await getFreshToken(user);

      await axios.delete(`${API_CHAT}instruksi/${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      // Hapus pesan dari state
      setChatMessages((prev) => prev.filter((msg) => msg.id_chat !== chatId));
      swal("Berhasil", "Pesan berhasil dihapus", "success");
    } catch (error) {
      console.error("Error deleting message:", error);
      swal("Error", "Gagal menghapus pesan", "error");
    }
  };

  // Format waktu untuk chat
  const formatChatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Format waktu: HH:mm
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const timeStr = `${hours}:${minutes}`;

    // Jika hari ini
    if (diffDays === 0) {
      return timeStr;
    }

    // Jika kemarin
    if (diffDays === 1) {
      return `Kemarin ${timeStr}`;
    }

    // Jika dalam seminggu
    if (diffDays < 7) {
      const dayNames = [
        "Minggu",
        "Senin",
        "Selasa",
        "Rabu",
        "Kamis",
        "Jumat",
        "Sabtu",
      ];
      return `${dayNames[date.getDay()]} ${timeStr}`;
    }

    // Jika lebih dari seminggu
    const day = date.getDate();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "Mei",
      "Jun",
      "Jul",
      "Agu",
      "Sep",
      "Okt",
      "Nov",
      "Des",
    ];
    const month = monthNames[date.getMonth()];

    // Jika tahun berbeda, tambahkan tahun
    if (date.getFullYear() !== now.getFullYear()) {
      return `${day} ${month} ${date.getFullYear()} ${timeStr}`;
    }

    return `${day} ${month} ${timeStr}`;
  };

  // Auto scroll ke bawah saat ada pesan baru
  useEffect(() => {
    if (chatEndRef.current && chatMessages.length > 0) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const renderPenerima = (rincianPenerima, instruksiItem) => {
    if (!Array.isArray(rincianPenerima) || rincianPenerima.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          Tidak ada penerima
        </MDTypography>
      );
    }

    if (rincianPenerima.length === 1) {
      return (
        <MDBox
          display="flex"
          alignItems="center"
          onClick={() =>
            handleOpenPenerimaDialog(rincianPenerima, instruksiItem)
          }
          sx={{ cursor: "pointer" }}
        >
          <MDAvatar
            src={
              rincianPenerima[0].uid_RSS
                ? `https://smartdh.rotendaokab.go.id/uploads/${rincianPenerima[0].uid_RSS}.jpg`
                : undefined
            }
            alt={rincianPenerima[0].nama}
            size="sm"
            sx={{ mr: 1 }}
          />
          <MDTypography variant="button" fontWeight="medium">
            {rincianPenerima[0].nama}
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <Button
        variant="contained"
        size="small"
        onClick={() => handleOpenPenerimaDialog(rincianPenerima, instruksiItem)}
      >
        {rincianPenerima.length} Penerima
      </Button>
    );
  };

  // Helper function untuk mengecek apakah instruksi approved
  const isInstruksiApproved = (rincianPenerima, pengirimId) => {
    if (!Array.isArray(rincianPenerima) || rincianPenerima.length === 0) {
      return false;
    }

    const penerimaLangsung = rincianPenerima.filter(
      (p) => p.asal_perintah_dari_id === pengirimId
    );

    if (penerimaLangsung.length === 0) {
      return false;
    }

    // Cek apakah semua penerima langsung memiliki status "selesai" (progress 100%)
    const semuaSelesai = penerimaLangsung.every(
      (p) => (p.status || "").toLowerCase() === "selesai"
    );

    if (!semuaSelesai) {
      return false;
    }

    // Cek apakah semua penerima langsung memiliki respon "Terima"
    const semuaTerima = penerimaLangsung.every(
      (p) => (p.respon_pemberi_instruksi || "").trim() === "Terima"
    );

    return semuaTerima;
  };

  // Render penerima langsung
  const renderPenerimaLangsung = (rincianPenerima, instruksiItem) => {
    if (!Array.isArray(rincianPenerima) || rincianPenerima.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    const penerimaLangsung = rincianPenerima.filter(
      (p) => p.asal_perintah_dari_id === userId
    );

    if (penerimaLangsung.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    if (penerimaLangsung.length === 1) {
      return (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDBox
            display="flex"
            alignItems="center"
            onClick={() =>
              handleOpenPenerimaDialog(
                rincianPenerima,
                instruksiItem,
                "langsung"
              )
            }
            sx={{ cursor: "pointer", flex: 1 }}
          >
            <MDAvatar
              src={
                penerimaLangsung[0].uid_RSS
                  ? `https://smartdh.rotendaokab.go.id/uploads/${penerimaLangsung[0].uid_RSS}.jpg`
                  : undefined
              }
              alt={penerimaLangsung[0].nama}
              size="sm"
              sx={{ mr: 1 }}
            />
            <MDTypography variant="button" fontWeight="medium">
              {penerimaLangsung[0].nama}
            </MDTypography>
          </MDBox>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenChat(instruksiItem, penerimaLangsung[0]);
            }}
            sx={{ ml: 0.5 }}
          >
            <ChatIcon fontSize="small" />
          </IconButton>
        </MDBox>
      );
    }

    return (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() =>
            handleOpenPenerimaDialog(rincianPenerima, instruksiItem, "langsung")
          }
        >
          {penerimaLangsung.length} Penerima
        </Button>
        <IconButton
          size="small"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            // Jika banyak penerima, buka dialog untuk pilih penerima dulu
            handleOpenPenerimaDialog(
              rincianPenerima,
              instruksiItem,
              "langsung"
            );
          }}
          title="Chat dengan penerima"
        >
          <ChatIcon fontSize="small" />
        </IconButton>
      </MDBox>
    );
  };

  // Render penerima lanjutan
  const renderPenerimaLanjutan = (rincianPenerima, instruksiItem) => {
    if (!Array.isArray(rincianPenerima) || rincianPenerima.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    const penerimaLanjutan = rincianPenerima.filter(
      (p) => p.asal_perintah_dari_id !== userId
    );

    if (penerimaLanjutan.length === 0) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    if (penerimaLanjutan.length === 1) {
      return (
        <MDBox
          display="flex"
          alignItems="center"
          onClick={() =>
            handleOpenPenerimaDialog(rincianPenerima, instruksiItem, "lanjutan")
          }
          sx={{ cursor: "pointer" }}
        >
          <MDAvatar
            src={
              penerimaLanjutan[0].uid_RSS
                ? `https://smartdh.rotendaokab.go.id/uploads/${penerimaLanjutan[0].uid_RSS}.jpg`
                : undefined
            }
            alt={penerimaLanjutan[0].nama}
            size="sm"
            sx={{ mr: 1 }}
          />
          <MDTypography variant="button" fontWeight="medium">
            {penerimaLanjutan[0].nama}
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <Button
        variant="outlined"
        size="small"
        onClick={() =>
          handleOpenPenerimaDialog(rincianPenerima, instruksiItem, "lanjutan")
        }
        sx={{
          backgroundColor: "#ffffff",
          color: "#000000",
          borderColor: "#000000",
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#000000",
            color: "#000000",
          },
        }}
      >
        {penerimaLanjutan.length} Penerima
      </Button>
    );
  };

  const renderDokumen = (dokumen) => {
    if (!dokumen) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }
    let dokumenArr = [];
    if (Array.isArray(dokumen)) {
      dokumenArr = dokumen;
    } else if (typeof dokumen === "string" && dokumen.trim().startsWith("[")) {
      try {
        dokumenArr = JSON.parse(dokumen);
      } catch (e) {
        dokumenArr = [];
      }
    }
    if (dokumenArr.length === 0) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }
    return (
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <DescriptionIcon sx={{ fontSize: 16, color: "#1565c0" }} />
        <MDTypography variant="caption" color="text">
          Dok Instruksi: {dokumenArr.length}
        </MDTypography>
        {dokumenArr.map((file, idx) => {
          const fileName =
            typeof file === "string" ? file.split("/").pop() : `Dok ${idx + 1}`;
          const fileExt = fileName?.split(".").pop()?.toLowerCase() || "";
          const isPdf = fileExt === "pdf";
          return (
            <IconButton
              key={idx}
              size="small"
              onClick={() => handleOpenFile(file)}
              sx={{ p: 0.5 }}
              title={fileName}
            >
              {isPdf ? (
                <PictureAsPdfIcon sx={{ fontSize: 14, color: "#f44336" }} />
              ) : (
                (() => {
                  const ext = fileExt;
                  if (
                    [
                      "png",
                      "jpg",
                      "jpeg",
                      "gif",
                      "bmp",
                      "svg",
                      "webp",
                    ].includes(ext)
                  )
                    return (
                      <ImageIcon sx={{ fontSize: 14, color: "#1976d2" }} />
                    );
                  if (["doc", "docx"].includes(ext))
                    return (
                      <DescriptionIcon
                        sx={{ fontSize: 14, color: "#1565c0" }}
                      />
                    );
                  if (["xls", "xlsx"].includes(ext))
                    return (
                      <DescriptionIcon
                        sx={{ fontSize: 14, color: "#388e3c" }}
                      />
                    );
                  if (["ppt", "pptx"].includes(ext))
                    return (
                      <DescriptionIcon
                        sx={{ fontSize: 14, color: "#ff9800" }}
                      />
                    );
                  return (
                    <InsertDriveFileIcon
                      sx={{ fontSize: 14, color: "#757575" }}
                    />
                  );
                })()
              )}
            </IconButton>
          );
        })}
      </MDBox>
    );
  };

  // Render dokumen hanya icon saja
  const renderDokumenIcon = (dokumen) => {
    if (!dokumen) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }
    let dokumenArr = [];
    if (Array.isArray(dokumen)) {
      dokumenArr = dokumen;
    } else if (typeof dokumen === "string" && dokumen.trim().startsWith("[")) {
      try {
        dokumenArr = JSON.parse(dokumen);
      } catch (e) {
        dokumenArr = [];
      }
    }
    if (dokumenArr.length === 0) {
      return (
        <MDTypography variant="caption" color="text">
          -
        </MDTypography>
      );
    }
    return (
      <MDBox display="flex" alignItems="center" gap={0.5}>
        {dokumenArr.map((file, idx) => {
          const fileName =
            typeof file === "string" ? file.split("/").pop() : `Dok ${idx + 1}`;
          const fileExt = fileName?.split(".").pop()?.toLowerCase() || "";
          const isPdf = fileExt === "pdf";
          return (
            <IconButton
              key={idx}
              size="small"
              onClick={() => handleOpenFile(file)}
              sx={{ p: 0.5 }}
              title={fileName}
            >
              {isPdf ? (
                <PictureAsPdfIcon sx={{ fontSize: 16, color: "#f44336" }} />
              ) : (
                (() => {
                  const ext = fileExt;
                  if (
                    [
                      "png",
                      "jpg",
                      "jpeg",
                      "gif",
                      "bmp",
                      "svg",
                      "webp",
                    ].includes(ext)
                  )
                    return (
                      <ImageIcon sx={{ fontSize: 16, color: "#1976d2" }} />
                    );
                  if (["doc", "docx"].includes(ext))
                    return (
                      <DescriptionIcon
                        sx={{ fontSize: 16, color: "#1565c0" }}
                      />
                    );
                  if (["xls", "xlsx"].includes(ext))
                    return (
                      <DescriptionIcon
                        sx={{ fontSize: 16, color: "#388e3c" }}
                      />
                    );
                  if (["ppt", "pptx"].includes(ext))
                    return (
                      <DescriptionIcon
                        sx={{ fontSize: 16, color: "#ff9800" }}
                      />
                    );
                  return (
                    <InsertDriveFileIcon
                      sx={{ fontSize: 16, color: "#757575" }}
                    />
                  );
                })()
              )}
            </IconButton>
          );
        })}
      </MDBox>
    );
  };

  // Render icon Approved
  const renderApproved = (rincianPenerima, pengirimId) => {
    const isApproved = isInstruksiApproved(rincianPenerima, pengirimId);
    if (!isApproved) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }
    return (
      <CheckCircleIcon
        sx={{
          color: "#00c875",
          fontSize: "1.5rem",
        }}
        titleAccess="Approved"
      />
    );
  };

  // Render Respon (menggabungkan ketepatan waktu dan approved status)
  const renderRespon = (item, rincianPenerima, pengirimId) => {
    const isApproved = isInstruksiApproved(rincianPenerima, pengirimId);

    if (isApproved) {
      // Jika approved, tampilkan icon approved + teks "Approved" + ketepatan waktu
      const ketepatanWaktu = renderKetepatanWaktu(item);
      return (
        <MDBox display="flex" flexDirection="column" gap={0.5}>
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <CheckCircleIcon
              sx={{
                color: "#00c875",
                fontSize: "1.2rem",
              }}
            />
            <MDTypography variant="caption" color="success" fontWeight="medium">
              Approved
            </MDTypography>
          </MDBox>
          {ketepatanWaktu}
        </MDBox>
      );
    } else {
      // Jika belum approved, tampilkan icon waiting + teks "waiting" + ketepatan waktu
      const ketepatanWaktu = renderKetepatanWaktu(item);
      return (
        <MDBox display="flex" flexDirection="column" gap={0.5}>
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <HourglassBottomIcon
              sx={{
                color: "#ffb300",
                fontSize: "1.2rem",
              }}
            />
            <MDTypography variant="caption" color="warning" fontWeight="medium">
              Waiting
            </MDTypography>
          </MDBox>
          {ketepatanWaktu}
        </MDBox>
      );
    }
  };

  // Hanya menghitung penerima langsung (asal_perintah_dari_id === pengirimId)
  const renderStatus = (item, pengirimId) => {
    if (!Array.isArray(item.rincian_penerima)) {
      return (
        <Chip
          label="Belum dikerjakan"
          size="small"
          sx={{
            backgroundColor: "#bdbdbd",
            color: "white",
            fontWeight: 600,
            fontSize: "0.7rem",
            height: "24px",
          }}
        />
      );
    }

    // Filter hanya penerima langsung (asal_perintah_dari_id === pengirimId)
    const penerimaLangsung = item.rincian_penerima.filter(
      (p) => p.asal_perintah_dari_id === pengirimId
    );

    if (penerimaLangsung.length === 0) {
      return (
        <Chip
          label="Tidak ada penerima langsung"
          size="small"
          sx={{
            backgroundColor: "#bdbdbd",
            color: "white",
            fontWeight: 600,
            fontSize: "0.7rem",
            height: "24px",
          }}
        />
      );
    }

    const totalPenerima = penerimaLangsung.length;
    const selesaiCount = penerimaLangsung.filter(
      (p) => (p.status || "").toLowerCase() === "selesai"
    ).length;

    let statusLabel = "Belum dikerjakan";
    let statusColor = "#bdbdbd";
    if (selesaiCount === 0) {
      statusLabel = "Belum dikerjakan";
      statusColor = "#bdbdbd";
    } else if (selesaiCount > 0 && selesaiCount < totalPenerima) {
      statusLabel = "On Progress";
      statusColor = "#ffb300";
    } else if (selesaiCount === totalPenerima && totalPenerima > 0) {
      statusLabel = "Completed";
      statusColor = "#4caf50";
    }
    return (
      <Chip
        label={statusLabel}
        size="small"
        sx={{
          backgroundColor: statusColor,
          color: "white",
          fontWeight: 600,
          fontSize: "0.7rem",
          height: "24px",
        }}
      />
    );
  };

  const renderKetepatanWaktu = (item) => {
    if (!item.tanggal_selesai) {
      return (
        <MDTypography variant="caption" color="text.secondary">
          -
        </MDTypography>
      );
    }

    const deadline = new Date(item.tanggal_selesai);
    const now = new Date();

    // Cek apakah instruksi sudah selesai (semua penerima langsung selesai)
    const penerimaLangsung = Array.isArray(item.rincian_penerima)
      ? item.rincian_penerima.filter((p) => p.asal_perintah_dari_id === userId)
      : [];

    const isCompleted =
      penerimaLangsung.length > 0 &&
      penerimaLangsung.every(
        (p) => (p.status || "").toLowerCase() === "selesai"
      );

    if (isCompleted) {
      // Instruksi sudah selesai - cek apakah tepat waktu
      // Ambil waktu selesai terakhir dari penerima langsung
      const waktuSelesaiList = penerimaLangsung
        .map((p) => p.waktu_selesai)
        .filter((w) => w && w !== null && w !== "")
        .map((w) => {
          const date = new Date(w);
          return isNaN(date.getTime()) ? null : date;
        })
        .filter((d) => d !== null);

      if (waktuSelesaiList.length === 0) {
        return (
          <MDTypography variant="caption" color="text.secondary">
            -
          </MDTypography>
        );
      }

      const waktuSelesaiTerakhir = new Date(
        Math.max(...waktuSelesaiList.map((d) => d.getTime()))
      );

      // Set deadline ke akhir hari (23:59:59) untuk perbandingan yang lebih akurat
      const deadlineEndOfDay = new Date(deadline);
      deadlineEndOfDay.setHours(23, 59, 59, 999);

      // Bandingkan waktu selesai dengan deadline
      if (waktuSelesaiTerakhir <= deadlineEndOfDay) {
        // Tepat waktu
        return (
          <MDTypography variant="caption" color="success" fontWeight="medium">
            Tepat Waktu
          </MDTypography>
        );
      } else {
        // Melewati waktu - hitung selisih
        const diffMs = waktuSelesaiTerakhir - deadlineEndOfDay;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(
          (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );

        let label = "Melewati Waktu";
        if (diffDays > 0) {
          label += ` ${diffDays} hari`;
        }
        if (diffHours > 0) {
          label += ` ${diffHours} jam`;
        }
        if (diffDays === 0 && diffHours === 0) {
          const diffMinutes = Math.floor(
            (diffMs % (1000 * 60 * 60)) / (1000 * 60)
          );
          if (diffMinutes > 0) {
            label += ` ${diffMinutes} menit`;
          }
        }

        return (
          <MDTypography variant="caption" color="error" fontWeight="medium">
            {label}
          </MDTypography>
        );
      }
    } else {
      // Instruksi belum selesai - cek apakah lewat deadline
      // Set deadline ke akhir hari (23:59:59) untuk perbandingan
      const deadlineEndOfDay = new Date(deadline);
      deadlineEndOfDay.setHours(23, 59, 59, 999);

      if (now > deadlineEndOfDay) {
        // Melewati waktu - hitung selisih dari deadline ke sekarang
        const diffMs = now - deadlineEndOfDay;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(
          (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );

        let label = "Melewati Waktu";
        if (diffDays > 0) {
          label += ` ${diffDays} hari`;
        }
        if (diffHours > 0) {
          label += ` ${diffHours} jam`;
        }
        if (diffDays === 0 && diffHours === 0) {
          const diffMinutes = Math.floor(
            (diffMs % (1000 * 60 * 60)) / (1000 * 60)
          );
          if (diffMinutes > 0) {
            label += ` ${diffMinutes} menit`;
          }
        }

        return (
          <MDTypography variant="caption" color="error" fontWeight="medium">
            {label}
          </MDTypography>
        );
      } else {
        // Belum lewat deadline - tidak perlu menampilkan apa-apa atau tampilkan info
        return (
          <MDTypography variant="caption" color="text.secondary">
            -
          </MDTypography>
        );
      }
    }
  };

  // Fungsi untuk render card penerima
  const renderPenerimaCard = (penerima, instruksiItem = null) => {
    const isCompleted =
      penerima.status && penerima.status.toLowerCase() === "selesai";
    const isInProgress =
      penerima.status &&
      (penerima.status.toLowerCase() === "proses" ||
        penerima.status.toLowerCase() === "on progress");

    // Parse file_tindak_lanjut
    let fileTLArr = [];
    if (penerima.file) {
      if (Array.isArray(penerima.file)) {
        fileTLArr = penerima.file;
      } else if (
        typeof penerima.file === "string" &&
        penerima.file.trim().startsWith("[")
      ) {
        try {
          fileTLArr = JSON.parse(penerima.file);
        } catch (e) {
          fileTLArr = [];
        }
      } else if (typeof penerima.file === "string") {
        fileTLArr = [penerima.file];
      }
    }

    // Cek apakah penerima adalah penerima langsung (untuk menampilkan icon chat)
    const isPenerimaLangsung = penerima.asal_perintah_dari_id === userId;

    return (
      <Card>
        <CardContent>
          {/* Asal Perintah */}
          {penerima.asal_perintah_nama && (
            <MDBox mb={2}>
              <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
                <MDAvatar
                  src={
                    penerima.instruksi?.asal_perintah_dari?.uid_RSS ||
                    penerima.asal_perintah_uid_RSS
                      ? `https://smartdh.rotendaokab.go.id/uploads/${
                          penerima.instruksi?.asal_perintah_dari?.uid_RSS ||
                          penerima.asal_perintah_uid_RSS
                        }.jpg`
                      : undefined
                  }
                  alt={penerima.asal_perintah_nama}
                  size="sm"
                />
                <MDBox>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Asal Perintah:
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {penerima.asal_perintah_nama}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox display="flex" alignItems="center" gap={1}>
                <ArrowForwardIcon
                  sx={{ fontSize: 20, color: "text.secondary" }}
                />
                <MDTypography variant="caption" color="text.secondary">
                  Penerima Instruksi
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          {/* Penerima Instruksi */}
          <MDBox mb={1}>
            <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
              <MDAvatar
                src={
                  penerima.instruksi?.penerima?.uid_RSS || penerima.uid_RSS
                    ? `https://smartdh.rotendaokab.go.id/uploads/${
                        penerima.instruksi?.penerima?.uid_RSS ||
                        penerima.uid_RSS
                      }.jpg`
                    : undefined
                }
                alt={penerima.nama}
                size="sm"
              />
              <MDBox sx={{ flex: 1 }}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  Penerima:
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="body2" color="text">
                    {penerima.nama}
                  </MDTypography>
                  {/* Icon Chat - hanya tampil untuk penerima langsung jika ada instruksiItem */}
                  {isPenerimaLangsung && instruksiItem && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Tutup dialog daftar penerima dulu, lalu buka chat setelah sedikit delay
                        handleClosePenerimaDialog();
                        setTimeout(() => {
                          handleOpenChat(instruksiItem, penerima);
                        }, 100);
                      }}
                      sx={{ ml: 0.5 }}
                      title="Chat dengan penerima"
                    >
                      <ChatIcon fontSize="small" />
                    </IconButton>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Tingkat Perintah */}
          {penerima.tingkat && (
            <MDBox mb={1}>
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
              >
                Tingkat Perintah:
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Level {penerima.tingkat}
              </MDTypography>
            </MDBox>
          )}

          {/* Status */}
          <MDBox mb={1}>
            <MDTypography
              variant="caption"
              color="text.secondary"
              fontWeight="medium"
              mb={0.5}
            >
              Status:
            </MDTypography>
            <MDBox display="flex" gap={1} flexWrap="wrap">
              {isCompleted ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Selesai"
                  color="success"
                  size="small"
                />
              ) : isInProgress ? (
                <Chip
                  icon={<HourglassBottomIcon />}
                  label="Proses"
                  color="warning"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<CancelIcon />}
                  label="Belum"
                  color="error"
                  size="small"
                />
              )}
            </MDBox>
          </MDBox>

          {/* Waktu Diterima */}
          {penerima.waktu_diterima && (
            <MDBox mb={1}>
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
              >
                Waktu Diterima:
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {formatDateTime(penerima.waktu_diterima)}
              </MDTypography>
            </MDBox>
          )}

          {/* Waktu Selesai */}
          {isCompleted && penerima.waktu_selesai && (
            <MDBox mb={1}>
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
              >
                Waktu Selesai:
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {formatDateTime(penerima.waktu_selesai)}
              </MDTypography>
            </MDBox>
          )}

          {/* Waktu Diselesaikan */}
          {isCompleted && penerima.waktu_diselesaikan && (
            <MDBox mb={1}>
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
              >
                Waktu Diselesaikan:
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {formatDateTime(penerima.waktu_diselesaikan)}
              </MDTypography>
            </MDBox>
          )}

          {/* Catatan Selesai */}
          {isCompleted && penerima.catatan_selesai && (
            <MDBox
              mt={1.5}
              sx={{
                p: 1,
                background: "#f0f7ff",
                borderRadius: 1,
                border: "1px solid #b3d9ff",
              }}
            >
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="600"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.6rem",
                }}
              >
                Catatan Selesai:
              </MDTypography>
              <MDTypography
                variant="caption"
                color="dark"
                sx={{
                  fontSize: "0.65rem",
                  lineHeight: 1.4,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {penerima.catatan_selesai}
              </MDTypography>
            </MDBox>
          )}

          {/* File Tindak Lanjut */}
          <MDBox mb={1}>
            <MDTypography
              variant="caption"
              color="text.secondary"
              fontWeight="medium"
              mb={0.5}
            >
              File Tindak Lanjut ({fileTLArr.length}):
            </MDTypography>
            {fileTLArr.length > 0 ? (
              <MDBox display="flex" flexDirection="column" gap={1}>
                {fileTLArr.map((file, idx) => {
                  const filename = file.split("/").pop();
                  return (
                    <Button
                      key={idx}
                      onClick={() => handleOpenFile(file)}
                      variant="contained"
                      size="small"
                      startIcon={getFileIcon(filename)}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      {filename}
                    </Button>
                  );
                })}
              </MDBox>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                Belum ada file tindak lanjut
              </MDTypography>
            )}
          </MDBox>

          {/* Catatan Tindak Lanjut */}
          {penerima.instruksi_tindak_lanjut && (
            <MDBox
              mt={1.5}
              sx={{
                p: 1,
                background: "#f8f9fa",
                borderRadius: 1,
                border: "1px solid #e9ecef",
              }}
            >
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="600"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.6rem",
                }}
              >
                Catatan Tindak Lanjut:
              </MDTypography>
              <MDTypography
                variant="caption"
                color="dark"
                sx={{
                  fontSize: "0.65rem",
                  lineHeight: 1.4,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {penerima.instruksi_tindak_lanjut}
              </MDTypography>
            </MDBox>
          )}

          {/* Respon Pemberi Instruksi - hanya tampil jika status Selesai dan waktu_diselesaikan terisi */}
          {isCompleted && penerima.waktu_diselesaikan && isPenerimaLangsung && (
            <MDBox mt={2}>
              {/* Tampilkan respon jika sudah ada */}
              {penerima.respon_pemberi_instruksi && (
                <MDBox
                  sx={{
                    p: 1.5,
                    background: "#f0f7ff",
                    borderRadius: 1,
                    border: "1px solid #b3d9ff",
                    mb: 2,
                  }}
                >
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="600"
                      >
                        Respon Anda:
                      </MDTypography>
                      <Chip
                        label={penerima.respon_pemberi_instruksi}
                        size="small"
                        color={
                          penerima.respon_pemberi_instruksi === "Terima"
                            ? "success"
                            : penerima.respon_pemberi_instruksi === "Tolak"
                            ? "error"
                            : "warning"
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </MDBox>
                    {/* Tombol Edit Respon */}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenResponDialog(penerima)}
                      sx={{
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        },
                      }}
                      title="Edit Respon"
                    >
                      <Icon sx={{ fontSize: "18px" }}>edit</Icon>
                    </IconButton>
                  </MDBox>
                  {penerima.respon_pemberi_instruksi === "Terima" &&
                    penerima.nilai && (
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="600"
                        >
                          Penilaian:
                        </MDTypography>
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Icon
                            key={rating}
                            sx={{
                              fontSize: "20px",
                              color:
                                penerima.nilai >= rating
                                  ? "#FFD700"
                                  : "#E0E0E0",
                            }}
                          >
                            star
                          </Icon>
                        ))}
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          ml={0.5}
                        >
                          ({penerima.nilai} bintang)
                        </MDTypography>
                      </MDBox>
                    )}
                </MDBox>
              )}

              {/* Tombol respon muncul jika belum ada respon, atau jika respon adalah Perbaiki/Tolak */}
              {(!penerima.respon_pemberi_instruksi ||
                penerima.respon_pemberi_instruksi === "Perbaiki" ||
                penerima.respon_pemberi_instruksi === "Tolak") && (
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => handleOpenResponDialog(penerima)}
                  startIcon={<Icon>rate_review</Icon>}
                  fullWidth
                >
                  {penerima.respon_pemberi_instruksi
                    ? "Ubah Respon"
                    : "Berikan Respon"}
                </MDButton>
              )}
            </MDBox>
          )}
        </CardContent>
      </Card>
    );
  };

  // Prepare data untuk tabel
  const tableData = useMemo(() => {
    return instruksiOleh.map((item) => ({
      ...item,
      // Tambahkan field yang diperlukan untuk tabel
    }));
  }, [instruksiOleh]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={4}>
        {/* Statistik Instruksi - Chips/Badge */}
        <MDBox mb={4}>
          <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
            {/* Filter Tahun dan Bulan */}
            <Autocomplete
              options={Array.from(
                { length: new Date().getFullYear() - 2024 + 1 },
                (_, i) => ({
                  label: (new Date().getFullYear() - i).toString(),
                  value: new Date().getFullYear() - i,
                })
              )}
              value={
                tahun
                  ? {
                      label: tahun.toString(),
                      value: tahun,
                    }
                  : null
              }
              onChange={(event, newValue) => {
                setTahun(newValue ? newValue.value : null);
              }}
              getOptionLabel={(option) =>
                typeof option === "object" ? option.label : option.toString()
              }
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              renderInput={(params) => (
                <MDInput {...params} label="Tahun" sx={{ minWidth: 120 }} />
              )}
              sx={{ minWidth: 120 }}
            />
            <Autocomplete
              options={[
                { label: "Semua Bulan", value: "semua" },
                { label: "Januari", value: 1 },
                { label: "Februari", value: 2 },
                { label: "Maret", value: 3 },
                { label: "April", value: 4 },
                { label: "Mei", value: 5 },
                { label: "Juni", value: 6 },
                { label: "Juli", value: 7 },
                { label: "Agustus", value: 8 },
                { label: "September", value: 9 },
                { label: "Oktober", value: 10 },
                { label: "November", value: 11 },
                { label: "Desember", value: 12 },
              ]}
              value={
                bulan
                  ? {
                      label:
                        bulan === "semua"
                          ? "Semua Bulan"
                          : [
                              "Januari",
                              "Februari",
                              "Maret",
                              "April",
                              "Mei",
                              "Juni",
                              "Juli",
                              "Agustus",
                              "September",
                              "Oktober",
                              "November",
                              "Desember",
                            ][bulan - 1],
                      value: bulan,
                    }
                  : null
              }
              onChange={(event, newValue) => {
                setBulan(newValue ? newValue.value : null);
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <MDInput {...params} label="Bulan" sx={{ minWidth: 150 }} />
              )}
              sx={{ minWidth: 150 }}
            />

            {/* Total Instruksi Dibuat */}
            <Chip
              label={`Total Instruksi Dibuat: ${instruksiOleh.length}`}
              color="info"
              size="medium"
              icon={<Icon>edit_note</Icon>}
              sx={{ fontSize: "0.875rem", fontWeight: "medium", px: 1 }}
            />

            {/* Instruksi Aktif */}
            <Chip
              label={`Instruksi Aktif: ${
                instruksiOleh.filter((item) => {
                  if (!Array.isArray(item.rincian_penerima)) return false;
                  // Filter hanya penerima langsung
                  const penerimaLangsung = item.rincian_penerima.filter(
                    (p) => p.asal_perintah_dari_id === userId
                  );
                  if (penerimaLangsung.length === 0) return false;
                  return penerimaLangsung.some(
                    (p) => (p.status || "").toLowerCase() !== "selesai"
                  );
                }).length
              }`}
              color="warning"
              size="medium"
              icon={<Icon>pending_actions</Icon>}
              sx={{ fontSize: "0.875rem", fontWeight: "medium", px: 1 }}
            />

            {/* Progress Rata-rata */}
            <Chip
              label={`Progress Rata-rata: ${(() => {
                const activeInstruksi = instruksiOleh.filter((item) => {
                  if (!Array.isArray(item.rincian_penerima)) return false;
                  const penerimaLangsung = item.rincian_penerima.filter(
                    (p) => p.asal_perintah_dari_id === userId
                  );
                  if (penerimaLangsung.length === 0) return false;
                  return penerimaLangsung.some(
                    (p) => (p.status || "").toLowerCase() !== "selesai"
                  );
                });

                if (activeInstruksi.length === 0) return "0%";

                const totalProgress = activeInstruksi.reduce((sum, item) => {
                  const penerimaLangsung = Array.isArray(item.rincian_penerima)
                    ? item.rincian_penerima.filter(
                        (p) => p.asal_perintah_dari_id === userId
                      )
                    : [];
                  const totalPenerima = penerimaLangsung.length;
                  const selesaiCount = penerimaLangsung.filter(
                    (p) => (p.status || "").toLowerCase() === "selesai"
                  ).length;
                  const progress =
                    totalPenerima > 0
                      ? Math.round((selesaiCount / totalPenerima) * 100)
                      : 0;
                  return sum + progress;
                }, 0);

                return Math.round(totalProgress / activeInstruksi.length) + "%";
              })()}`}
              color="success"
              size="medium"
              icon={<Icon>trending_up</Icon>}
              sx={{ fontSize: "0.875rem", fontWeight: "medium", px: 1 }}
            />

            {/* Deadline ≤ 3 Hari */}
            <Chip
              label={`Deadline ≤ 3 Hari: ${(() => {
                const today = new Date();
                const deadlineMendekati = instruksiOleh.filter((item) => {
                  if (!item.tanggal_selesai) return false;
                  const tglSelesai = new Date(item.tanggal_selesai);
                  const diffTime =
                    tglSelesai.setHours(0, 0, 0, 0) -
                    today.setHours(0, 0, 0, 0);
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  return diffDays <= 3 && diffDays >= 0;
                }).length;
                return deadlineMendekati;
              })()}`}
              color="error"
              size="medium"
              icon={<Icon>schedule</Icon>}
              sx={{ fontSize: "0.875rem", fontWeight: "medium", px: 1 }}
            />
          </MDBox>
        </MDBox>

        {/* Status Instruksi - Chips/Badge */}
        <MDBox mb={4}>
          <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="center">
            {(() => {
              const statusStats = {
                selesai: 0,
                proses: 0,
                belum: 0,
                lewat: 0,
              };

              const today = new Date();
              instruksiOleh.forEach((item) => {
                if (Array.isArray(item.rincian_penerima)) {
                  // Filter hanya penerima langsung
                  const penerimaLangsung = item.rincian_penerima.filter(
                    (p) => p.asal_perintah_dari_id === userId
                  );

                  if (penerimaLangsung.length === 0) return;

                  const totalPenerima = penerimaLangsung.length;
                  const selesaiCount = penerimaLangsung.filter(
                    (p) => (p.status || "").toLowerCase() === "selesai"
                  ).length;

                  if (selesaiCount === totalPenerima && totalPenerima > 0) {
                    statusStats.selesai++;
                  } else if (selesaiCount > 0) {
                    statusStats.proses++;
                  } else {
                    statusStats.belum++;
                  }

                  // Cek deadline
                  if (item.tanggal_selesai) {
                    const tglSelesai = new Date(item.tanggal_selesai);
                    const diffTime =
                      tglSelesai.setHours(0, 0, 0, 0) -
                      today.setHours(0, 0, 0, 0);
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    if (diffDays < 0) {
                      statusStats.lewat++;
                    }
                  }
                }
              });

              const statusItems = [
                {
                  label: "Selesai",
                  count: statusStats.selesai,
                  color: "success",
                  icon: "check_circle",
                  statusKey: "selesai",
                },
                {
                  label: "Dalam Proses",
                  count: statusStats.proses,
                  color: "warning",
                  icon: "pending",
                  statusKey: "dalamproses",
                },
                {
                  label: "Belum Dimulai",
                  count: statusStats.belum,
                  color: "error",
                  icon: "schedule",
                  statusKey: "belumdimulai",
                },
                {
                  label: "Lewat Deadline",
                  count: statusStats.lewat,
                  color: "info",
                  icon: "warning",
                  statusKey: "lewatdeadline",
                },
              ];

              return statusItems.map((item) => (
                <Chip
                  key={item.label}
                  label={`${item.label}: ${item.count}`}
                  color={item.color}
                  size="medium"
                  icon={<Icon>{item.icon}</Icon>}
                  onClick={() => {
                    handleOpenStatusDialog(item.statusKey);
                  }}
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: "medium",
                    px: 1,
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                />
              ));
            })()}
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ background: "#fff" }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                p={3}
                variant="gradient"
                bgColor="info"
                color="white"
                borderRadius="lg"
                boxShadow="lg"
              >
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  Daftar Instruksi Oleh Anda
                </MDTypography>
                <MDButton
                  variant="outlined"
                  onClick={() =>
                    navigate("/task-management/tugas/instruksi-baru")
                  }
                  sx={{
                    boxShadow: 1,
                    borderRadius: 2,
                    fontWeight: "bold",
                    textTransform: "none",
                    px: 3,
                    py: 1.5,
                    backgroundColor: "#ffffff",
                    color: "#000000",
                    border: "1px solid #000000",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#000000",
                      color: "#000000",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Icon sx={{ fontWeight: "bold", mr: 1, color: "#000000" }}>
                    add
                  </Icon>
                  Buat Instruksi
                </MDButton>
              </MDBox>

              {/* Loading Spinner */}
              {loading ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={4}
                >
                  <CircularProgress />
                </MDBox>
              ) : (
                <>
                  {instruksiOleh.length === 0 && (
                    <MDTypography variant="body2" color="text">
                      Belum ada instruksi.
                    </MDTypography>
                  )}
                  {instruksiOleh.length > 0 && (
                    <>
                      {isMobile ? (
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          gap={2}
                          px={2}
                        >
                          {instruksiOleh.map((item) => {
                            // Hitung progres: hanya penerima langsung
                            const penerimaLangsung = Array.isArray(
                              item.rincian_penerima
                            )
                              ? item.rincian_penerima.filter(
                                  (p) => p.asal_perintah_dari_id === userId
                                )
                              : [];
                            const totalPenerima = penerimaLangsung.length;
                            const selesaiCount = penerimaLangsung.filter(
                              (p) =>
                                (p.status || "").toLowerCase() === "selesai"
                            ).length;
                            const progress =
                              totalPenerima > 0
                                ? Math.round(
                                    (selesaiCount / totalPenerima) * 100
                                  )
                                : 0;
                            let statusLabel = "Belum dikerjakan";
                            let statusColor = "#bdbdbd";
                            if (totalPenerima === 0) {
                              statusLabel = "Tidak ada penerima langsung";
                              statusColor = "#bdbdbd";
                            } else if (selesaiCount === 0) {
                              statusLabel = "Belum dikerjakan";
                              statusColor = "#bdbdbd";
                            } else if (
                              selesaiCount > 0 &&
                              selesaiCount < totalPenerima
                            ) {
                              statusLabel = "On Progress";
                              statusColor = "#ffb300";
                            } else if (
                              selesaiCount === totalPenerima &&
                              totalPenerima > 0
                            ) {
                              statusLabel = "Completed";
                              statusColor = "#4caf50";
                            }

                            let dokumenArr = [];
                            if (Array.isArray(item.dokumen)) {
                              dokumenArr = item.dokumen;
                            } else if (
                              typeof item.dokumen === "string" &&
                              item.dokumen.trim().startsWith("[")
                            ) {
                              try {
                                dokumenArr = JSON.parse(item.dokumen);
                              } catch (e) {
                                dokumenArr = [];
                              }
                            }

                            return (
                              <Card
                                key={item.instruksi_id}
                                sx={{
                                  mb: 2,
                                  p: 0,
                                  width: "100%",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                  borderRadius: 2,
                                  border: "1px solid #e8e8e8",
                                  transition: "all 0.2s ease-in-out",
                                  background: "#ffffff",
                                  "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                    borderColor: "#d0d0d0",
                                  },
                                }}
                              >
                                {/* Header Section - Monday.com Style */}
                                <MDBox
                                  sx={{
                                    p: 2.5,
                                    pb: 2,
                                    borderBottom: "1px solid #f5f5f5",
                                    background: "#ffffff",
                                  }}
                                >
                                  {/* Baris 1: Profil dan Nama */}
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    mb={1.5}
                                  >
                                    <MDAvatar
                                      src={
                                        item.pengirim?.uid_RSS
                                          ? `https://smartdh.rotendaokab.go.id/uploads/${item.pengirim.uid_RSS}.jpg`
                                          : undefined
                                      }
                                      size="sm"
                                      sx={{
                                        mr: 1.5,
                                        border: "2px solid #ffffff",
                                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <MDBox sx={{ minWidth: 0, flex: 1 }}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="600"
                                        color="dark"
                                        sx={{
                                          fontSize: "0.875rem",
                                          wordBreak: "break-word",
                                          overflowWrap: "break-word",
                                        }}
                                      >
                                        {item.pengirim?.nama || "-"}
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text"
                                        sx={{
                                          display: "block",
                                          fontSize: "0.75rem",
                                          color: "#666",
                                        }}
                                      >
                                        {formatDate(item.tanggal)}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>

                                  {/* Baris 2: Prioritas dan Status */}
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                    mb={1.5}
                                  >
                                    {/* Ketepatan Waktu */}
                                    <MDBox
                                      sx={{
                                        minWidth: "60px",
                                        maxWidth: "80px",
                                        flexShrink: 0,
                                        wordBreak: "break-word",
                                        overflowWrap: "break-word",
                                      }}
                                    >
                                      <MDBox
                                        sx={{
                                          "& .MuiTypography-root": {
                                            fontSize: "0.65rem !important",
                                            lineHeight: 1.3,
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            whiteSpace: "normal",
                                            display: "block",
                                          },
                                        }}
                                      >
                                        {renderKetepatanWaktu(item)}
                                      </MDBox>
                                    </MDBox>

                                    {/* Prioritas */}
                                    <MDBox
                                      sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 1,
                                        background:
                                          PRIORITAS_COLOR[item.prioritas] ||
                                          "#f5f5f5",
                                        border: `1px solid ${
                                          PRIORITAS_COLOR[item.prioritas] ||
                                          "#e0e0e0"
                                        }`,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        color="white"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "0.7rem",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {item.prioritas}
                                      </MDTypography>
                                    </MDBox>

                                    {/* Status */}
                                    <MDBox
                                      sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 1,
                                        background: statusColor,
                                        border: `1px solid ${statusColor}`,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        color="white"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "0.7rem",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {statusLabel}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>

                                  {/* Progress Bar - Monday.com Style */}
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1.5}
                                  >
                                    <MDBox flex={1}>
                                      <MDBox
                                        sx={{
                                          width: "100%",
                                          background: "#f0f0f0",
                                          borderRadius: 1,
                                          height: 6,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <MDBox
                                          sx={{
                                            width: `${progress}%`,
                                            background:
                                              progress === 100
                                                ? "linear-gradient(90deg, #00c875, #00d884)"
                                                : "linear-gradient(90deg, #0073ea, #1a8cff)",
                                            height: "100%",
                                            borderRadius: 1,
                                            transition:
                                              "width 0.3s ease-in-out",
                                          }}
                                        />
                                      </MDBox>
                                    </MDBox>
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        color: "#666",
                                        minWidth: 32,
                                      }}
                                    >
                                      {progress}%
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>

                                {/* Content Section - Monday.com Style */}
                                <MDBox sx={{ p: 2.5 }}>
                                  {item.perihal && (
                                    <MDBox
                                      onClick={() =>
                                        handleOpenPerihalDialog(item.perihal)
                                      }
                                      sx={{
                                        cursor: "pointer",
                                        mb: 2,
                                        "&:hover": {
                                          textDecoration: "underline",
                                          color: "primary.main",
                                        },
                                      }}
                                    >
                                      <MDTypography
                                        variant="h6"
                                        color="dark"
                                        fontWeight="600"
                                        sx={{
                                          fontSize: "1rem",
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {item.perihal.length > 100
                                          ? item.perihal.substring(0, 100) +
                                            "..."
                                          : item.perihal}
                                      </MDTypography>
                                    </MDBox>
                                  )}

                                  <MDBox
                                    mb={2.5}
                                    onClick={() =>
                                      handleOpenIsiInstruksiDialog(
                                        item.isi_instruksi
                                      )
                                    }
                                    sx={{
                                      background: "#fafbfc",
                                      p: 2,
                                      borderRadius: 1.5,
                                      border: "1px solid #e8e8e8",
                                      cursor: "pointer",
                                      "&:hover": {
                                        borderColor: "primary.main",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                      },
                                    }}
                                  >
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: item.isi_instruksi || "",
                                      }}
                                      style={{
                                        fontSize: "0.875rem",
                                        color: "#333",
                                        wordBreak: "break-word",
                                        lineHeight: 1.5,
                                        maxHeight: "150px",
                                        overflow: "hidden",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 5,
                                        WebkitBoxOrient: "vertical",
                                      }}
                                    />
                                    <MDTypography
                                      variant="caption"
                                      color="primary"
                                      sx={{
                                        mt: 1,
                                        display: "block",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Klik untuk melihat lengkap →
                                    </MDTypography>
                                  </MDBox>

                                  {dokumenArr.length > 0 && (
                                    <MDBox mb={2.5}>
                                      <MDTypography
                                        variant="button"
                                        color="text"
                                        fontWeight="600"
                                        mb={1.5}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          fontSize: "0.8rem",
                                          color: "#666",
                                        }}
                                      >
                                        <Icon sx={{ fontSize: 16 }}>
                                          attach_file
                                        </Icon>
                                        Dokumen ({dokumenArr.length})
                                      </MDTypography>
                                      <MDBox
                                        display="flex"
                                        flexDirection="row"
                                        gap={1}
                                        flexWrap="wrap"
                                      >
                                        {dokumenArr.map((link, idx) => {
                                          const filename = link
                                            .split("/")
                                            .pop();
                                          return (
                                            <Button
                                              key={idx}
                                              href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                              target="_blank"
                                              size="small"
                                              sx={{
                                                textTransform: "none",
                                                justifyContent: "flex-start",
                                                minWidth: 80,
                                                background: "#ffffff",
                                                color: "#666",
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                fontSize: "0.75rem",
                                                "&:hover": {
                                                  background: "#f5f5f5",
                                                  borderColor: "#0073ea",
                                                  color: "#0073ea",
                                                },
                                              }}
                                              variant="outlined"
                                              startIcon={getFileIcon(filename)}
                                            >
                                              {`Dok ${idx + 1}`}
                                            </Button>
                                          );
                                        })}
                                      </MDBox>
                                    </MDBox>
                                  )}

                                  {/* Penerima Summary - Tampilan Langsung dan Lanjutan */}
                                  {Array.isArray(item.rincian_penerima) &&
                                    item.rincian_penerima.length > 0 && (
                                      <MDBox>
                                        {/* Penerima Langsung */}
                                        {penerimaLangsung.length > 0 && (
                                          <MDBox
                                            sx={{
                                              border: "2px solid #2196f3",
                                              borderRadius: 1.5,
                                              overflow: "hidden",
                                              background: "#ffffff",
                                              mb: 3,
                                              boxShadow:
                                                "0 2px 8px rgba(33, 150, 243, 0.1)",
                                            }}
                                          >
                                            {/* Header Penerima Langsung */}
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              p={2}
                                              sx={{
                                                background: "#e3f2fd",
                                                borderBottom:
                                                  "1px solid #e8e8e8",
                                              }}
                                            >
                                              <MDBox
                                                display="flex"
                                                alignItems="center"
                                              >
                                                <MDTypography
                                                  variant="button"
                                                  color="primary"
                                                  fontWeight="600"
                                                  mr={1.5}
                                                  sx={{ fontSize: "0.8rem" }}
                                                >
                                                  <Icon
                                                    sx={{
                                                      fontSize: 16,
                                                      mr: 0.5,
                                                    }}
                                                  >
                                                    person
                                                  </Icon>
                                                  Penerima Langsung (
                                                  {penerimaLangsung.length})
                                                </MDTypography>
                                              </MDBox>
                                              <MDTypography
                                                variant="caption"
                                                color="primary"
                                                sx={{ fontWeight: 600 }}
                                              >
                                                {selesaiCount}/{totalPenerima}{" "}
                                                selesai
                                              </MDTypography>
                                            </MDBox>

                                            {/* List Penerima Langsung */}
                                            <MDBox
                                              sx={{ background: "#fafbfc" }}
                                            >
                                              {penerimaLangsung.map(
                                                (penerima, idx) => {
                                                  // Status dengan icon
                                                  const getStatusDisplay =
                                                    () => {
                                                      const status = (
                                                        penerima &&
                                                        penerima.status
                                                          ? penerima.status
                                                          : "-"
                                                      ).toLowerCase();
                                                      if (
                                                        status === "selesai"
                                                      ) {
                                                        return {
                                                          icon: (
                                                            <CheckCircleIcon
                                                              sx={{
                                                                color:
                                                                  "#00c875",
                                                                fontSize: 16,
                                                              }}
                                                            />
                                                          ),
                                                          label: "Selesai",
                                                          color: "success",
                                                        };
                                                      } else if (
                                                        status === "belum"
                                                      ) {
                                                        return {
                                                          icon: (
                                                            <CancelIcon
                                                              sx={{
                                                                color:
                                                                  "#ff5c5c",
                                                                fontSize: 16,
                                                              }}
                                                            />
                                                          ),
                                                          label: "Belum",
                                                          color: "error",
                                                        };
                                                      } else if (
                                                        status === "proses" ||
                                                        status === "on progress"
                                                      ) {
                                                        return {
                                                          icon: (
                                                            <HourglassBottomIcon
                                                              sx={{
                                                                color:
                                                                  "#ffb300",
                                                                fontSize: 16,
                                                              }}
                                                            />
                                                          ),
                                                          label: "Proses",
                                                          color: "warning",
                                                        };
                                                      } else {
                                                        return {
                                                          icon: (
                                                            <Icon
                                                              sx={{
                                                                color: "#666",
                                                                fontSize: 16,
                                                              }}
                                                            >
                                                              help
                                                            </Icon>
                                                          ),
                                                          label: status,
                                                          color: "text",
                                                        };
                                                      }
                                                    };

                                                  const statusDisplay =
                                                    getStatusDisplay();

                                                  // File tindak lanjut
                                                  let files = [];
                                                  if (
                                                    Array.isArray(penerima.file)
                                                  ) {
                                                    files = penerima.file;
                                                  } else if (
                                                    typeof penerima.file ===
                                                      "string" &&
                                                    penerima.file
                                                      .trim()
                                                      .startsWith("[")
                                                  ) {
                                                    try {
                                                      files = JSON.parse(
                                                        penerima.file
                                                      );
                                                    } catch (e) {
                                                      files = [];
                                                    }
                                                  }

                                                  return (
                                                    <MDBox
                                                      key={`penerima-${
                                                        item.instruksi_id
                                                      }-${
                                                        penerima.user_id || idx
                                                      }`}
                                                      sx={{
                                                        borderBottom:
                                                          idx <
                                                          penerimaLangsung.length -
                                                            1
                                                            ? "1px solid #e8e8e8"
                                                            : "none",
                                                        p: 2,
                                                        background: "#ffffff",
                                                      }}
                                                    >
                                                      <Grid
                                                        container
                                                        spacing={2}
                                                      >
                                                        {/* Info Penerima */}
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          md={4}
                                                        >
                                                          <MDBox
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1.5}
                                                          >
                                                            <MDAvatar
                                                              src={
                                                                penerima
                                                                  .instruksi
                                                                  ?.penerima
                                                                  ?.uid_RSS ||
                                                                penerima.uid_RSS
                                                                  ? `https://smartdh.rotendaokab.go.id/uploads/${
                                                                      penerima
                                                                        .instruksi
                                                                        ?.penerima
                                                                        ?.uid_RSS ||
                                                                      penerima.uid_RSS
                                                                    }.jpg`
                                                                  : undefined
                                                              }
                                                              size="sm"
                                                              sx={{
                                                                border:
                                                                  "2px solid #ffffff",
                                                                boxShadow:
                                                                  "0 1px 3px rgba(0,0,0,0.1)",
                                                              }}
                                                            />
                                                            <MDBox>
                                                              <MDTypography
                                                                variant="button"
                                                                fontWeight="600"
                                                                color="dark"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.8rem",
                                                                }}
                                                              >
                                                                {penerima.nama ||
                                                                  "-"}
                                                              </MDTypography>
                                                              <MDBox
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                              >
                                                                {
                                                                  statusDisplay.icon
                                                                }
                                                                <MDTypography
                                                                  variant="caption"
                                                                  color={
                                                                    statusDisplay.color
                                                                  }
                                                                  fontWeight="600"
                                                                  sx={{
                                                                    fontSize:
                                                                      "0.65rem",
                                                                  }}
                                                                >
                                                                  {
                                                                    statusDisplay.label
                                                                  }
                                                                </MDTypography>
                                                              </MDBox>
                                                            </MDBox>
                                                          </MDBox>
                                                        </Grid>

                                                        {/* Detail Info */}
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          md={4}
                                                        >
                                                          <MDBox
                                                            display="flex"
                                                            flexDirection="column"
                                                            gap={0.5}
                                                          >
                                                            <MDBox
                                                              display="flex"
                                                              alignItems="center"
                                                              gap={1}
                                                              sx={{
                                                                p: 0.5,
                                                                background:
                                                                  "#fafbfc",
                                                                borderRadius: 1,
                                                                border:
                                                                  "1px solid #e8e8e8",
                                                              }}
                                                            >
                                                              <MDTypography
                                                                variant="caption"
                                                                color="text"
                                                                fontWeight="600"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.6rem",
                                                                }}
                                                              >
                                                                Tingkat:
                                                              </MDTypography>
                                                              <MDTypography
                                                                variant="caption"
                                                                color="dark"
                                                                fontWeight="600"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.6rem",
                                                                }}
                                                              >
                                                                {penerima &&
                                                                penerima.tingkat
                                                                  ? penerima.tingkat
                                                                  : "-"}
                                                              </MDTypography>
                                                            </MDBox>

                                                            {penerima.waktu_diterima && (
                                                              <MDBox
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                                sx={{
                                                                  p: 0.5,
                                                                  background:
                                                                    "#fafbfc",
                                                                  borderRadius: 1,
                                                                  border:
                                                                    "1px solid #e8e8e8",
                                                                }}
                                                              >
                                                                <MDTypography
                                                                  variant="caption"
                                                                  color="text"
                                                                  fontWeight="600"
                                                                  sx={{
                                                                    fontSize:
                                                                      "0.6rem",
                                                                  }}
                                                                >
                                                                  Diterima:
                                                                </MDTypography>
                                                                <MDTypography
                                                                  variant="caption"
                                                                  color="dark"
                                                                  fontWeight="600"
                                                                  sx={{
                                                                    fontSize:
                                                                      "0.6rem",
                                                                  }}
                                                                >
                                                                  {formatDate(
                                                                    penerima.waktu_diterima
                                                                  )}
                                                                </MDTypography>
                                                              </MDBox>
                                                            )}

                                                            {penerima.waktu_selesai && (
                                                              <MDBox
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                                sx={{
                                                                  p: 0.5,
                                                                  background:
                                                                    "#fafbfc",
                                                                  borderRadius: 1,
                                                                  border:
                                                                    "1px solid #e8e8e8",
                                                                }}
                                                              >
                                                                <MDTypography
                                                                  variant="caption"
                                                                  color="text"
                                                                  fontWeight="600"
                                                                  sx={{
                                                                    fontSize:
                                                                      "0.6rem",
                                                                  }}
                                                                >
                                                                  Selesai:
                                                                </MDTypography>
                                                                <MDTypography
                                                                  variant="caption"
                                                                  color="dark"
                                                                  fontWeight="600"
                                                                  sx={{
                                                                    fontSize:
                                                                      "0.6rem",
                                                                  }}
                                                                >
                                                                  {formatDate(
                                                                    penerima.waktu_selesai
                                                                  )}
                                                                </MDTypography>
                                                              </MDBox>
                                                            )}
                                                          </MDBox>
                                                        </Grid>

                                                        {/* File Tindak Lanjut */}
                                                        <Grid
                                                          item
                                                          xs={12}
                                                          md={4}
                                                        >
                                                          {files.length > 0 && (
                                                            <MDBox>
                                                              <MDTypography
                                                                variant="caption"
                                                                color="text"
                                                                fontWeight="600"
                                                                sx={{
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  gap: 0.5,
                                                                  mb: 0.5,
                                                                  fontSize:
                                                                    "0.6rem",
                                                                }}
                                                              >
                                                                <Icon
                                                                  sx={{
                                                                    fontSize: 10,
                                                                  }}
                                                                >
                                                                  attach_file
                                                                </Icon>
                                                                File (
                                                                {files.length}
                                                                ):
                                                              </MDTypography>
                                                              <MDBox
                                                                display="flex"
                                                                flexDirection="column"
                                                                gap={0.25}
                                                              >
                                                                {files
                                                                  .slice(0, 2)
                                                                  .map(
                                                                    (
                                                                      file,
                                                                      fileIdx
                                                                    ) => {
                                                                      const filename =
                                                                        file
                                                                          .split(
                                                                            "/"
                                                                          )
                                                                          .pop();
                                                                      return (
                                                                        <Button
                                                                          key={
                                                                            fileIdx
                                                                          }
                                                                          onClick={() =>
                                                                            handleOpenFile(
                                                                              file
                                                                            )
                                                                          }
                                                                          size="small"
                                                                          sx={{
                                                                            textTransform:
                                                                              "none",
                                                                            justifyContent:
                                                                              "flex-start",
                                                                            fontSize:
                                                                              "0.6rem",
                                                                            minWidth:
                                                                              "auto",
                                                                            px: 0.75,
                                                                            py: 0.25,
                                                                            background:
                                                                              "#ffffff",
                                                                            color:
                                                                              "#666",
                                                                            border:
                                                                              "1px solid #e0e0e0",
                                                                            borderRadius: 0.5,
                                                                            "&:hover":
                                                                              {
                                                                                background:
                                                                                  "#f5f5f5",
                                                                                borderColor:
                                                                                  "#0073ea",
                                                                                color:
                                                                                  "#0073ea",
                                                                              },
                                                                          }}
                                                                          variant="outlined"
                                                                          startIcon={getFileIcon(
                                                                            filename
                                                                          )}
                                                                        >
                                                                          {filename.length >
                                                                          20
                                                                            ? filename.substring(
                                                                                0,
                                                                                20
                                                                              ) +
                                                                              "..."
                                                                            : filename}
                                                                        </Button>
                                                                      );
                                                                    }
                                                                  )}
                                                                {files.length >
                                                                  2 && (
                                                                  <MDTypography
                                                                    variant="caption"
                                                                    color="text"
                                                                    sx={{
                                                                      fontSize:
                                                                        "0.55rem",
                                                                      fontStyle:
                                                                        "italic",
                                                                    }}
                                                                  >
                                                                    +
                                                                    {files.length -
                                                                      2}{" "}
                                                                    file lagi
                                                                  </MDTypography>
                                                                )}
                                                              </MDBox>
                                                            </MDBox>
                                                          )}
                                                        </Grid>
                                                      </Grid>

                                                      {/* Catatan Tindak Lanjut */}
                                                      {penerima.instruksi_tindak_lanjut && (
                                                        <MDBox
                                                          mt={1.5}
                                                          sx={{
                                                            p: 1,
                                                            background:
                                                              "#f8f9fa",
                                                            borderRadius: 1,
                                                            border:
                                                              "1px solid #e9ecef",
                                                          }}
                                                        >
                                                          <MDTypography
                                                            variant="caption"
                                                            color="text"
                                                            fontWeight="600"
                                                            sx={{
                                                              display: "block",
                                                              mb: 0.5,
                                                              fontSize:
                                                                "0.6rem",
                                                            }}
                                                          >
                                                            Catatan:
                                                          </MDTypography>
                                                          <MDTypography
                                                            variant="caption"
                                                            color="dark"
                                                            sx={{
                                                              fontSize:
                                                                "0.65rem",
                                                              lineHeight: 1.4,
                                                              display:
                                                                "-webkit-box",
                                                              WebkitLineClamp: 2,
                                                              WebkitBoxOrient:
                                                                "vertical",
                                                              overflow:
                                                                "hidden",
                                                              textOverflow:
                                                                "ellipsis",
                                                            }}
                                                          >
                                                            {
                                                              penerima.instruksi_tindak_lanjut
                                                            }
                                                          </MDTypography>
                                                        </MDBox>
                                                      )}
                                                    </MDBox>
                                                  );
                                                }
                                              )}
                                            </MDBox>
                                          </MDBox>
                                        )}

                                        {/* Penerima Lanjutan */}
                                        {item.rincian_penerima.filter(
                                          (p) =>
                                            p.asal_perintah_dari_id !== userId
                                        ).length > 0 && (
                                          <MDBox
                                            sx={{
                                              border: "2px solid #9c27b0",
                                              borderRadius: 1.5,
                                              overflow: "hidden",
                                              background: "#ffffff",
                                              mt: 2,
                                              boxShadow:
                                                "0 2px 8px rgba(156, 39, 176, 0.1)",
                                            }}
                                          >
                                            {/* Header Penerima Lanjutan */}
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              p={2}
                                              sx={{
                                                background: "#f3e5f5",
                                                borderBottom:
                                                  "1px solid #e8e8e8",
                                              }}
                                            >
                                              <MDBox
                                                display="flex"
                                                alignItems="center"
                                              >
                                                <MDTypography
                                                  variant="button"
                                                  color="secondary"
                                                  fontWeight="600"
                                                  mr={1.5}
                                                  sx={{ fontSize: "0.8rem" }}
                                                >
                                                  <Icon
                                                    sx={{
                                                      fontSize: 16,
                                                      mr: 0.5,
                                                    }}
                                                  >
                                                    people
                                                  </Icon>
                                                  Penerima Lanjutan (
                                                  {
                                                    item.rincian_penerima.filter(
                                                      (p) =>
                                                        p.asal_perintah_dari_id !==
                                                        userId
                                                    ).length
                                                  }
                                                  )
                                                </MDTypography>
                                              </MDBox>
                                            </MDBox>

                                            {/* List Penerima Lanjutan */}
                                            <MDBox
                                              sx={{ background: "#fafbfc" }}
                                            >
                                              {item.rincian_penerima
                                                .filter(
                                                  (p) =>
                                                    p.asal_perintah_dari_id !==
                                                    userId
                                                )
                                                .map((penerima, idx) => {
                                                  const getStatusDisplay =
                                                    () => {
                                                      const status = (
                                                        penerima &&
                                                        penerima.status
                                                          ? penerima.status
                                                          : "-"
                                                      ).toLowerCase();
                                                      if (
                                                        status === "selesai"
                                                      ) {
                                                        return {
                                                          icon: (
                                                            <CheckCircleIcon
                                                              sx={{
                                                                color:
                                                                  "#00c875",
                                                                fontSize: 16,
                                                              }}
                                                            />
                                                          ),
                                                          label: "Selesai",
                                                          color: "success",
                                                        };
                                                      } else if (
                                                        status === "belum"
                                                      ) {
                                                        return {
                                                          icon: (
                                                            <CancelIcon
                                                              sx={{
                                                                color:
                                                                  "#ff5c5c",
                                                                fontSize: 16,
                                                              }}
                                                            />
                                                          ),
                                                          label: "Belum",
                                                          color: "error",
                                                        };
                                                      } else if (
                                                        status === "proses" ||
                                                        status === "on progress"
                                                      ) {
                                                        return {
                                                          icon: (
                                                            <HourglassBottomIcon
                                                              sx={{
                                                                color:
                                                                  "#ffb300",
                                                                fontSize: 16,
                                                              }}
                                                            />
                                                          ),
                                                          label: "Proses",
                                                          color: "warning",
                                                        };
                                                      } else {
                                                        return {
                                                          icon: (
                                                            <Icon
                                                              sx={{
                                                                color: "#666",
                                                                fontSize: 16,
                                                              }}
                                                            >
                                                              help
                                                            </Icon>
                                                          ),
                                                          label: status,
                                                          color: "text",
                                                        };
                                                      }
                                                    };

                                                  const statusDisplay =
                                                    getStatusDisplay();

                                                  return (
                                                    <MDBox
                                                      key={`penerima-lanjutan-${
                                                        item.instruksi_id
                                                      }-${
                                                        penerima.user_id || idx
                                                      }`}
                                                      sx={{
                                                        borderBottom:
                                                          idx <
                                                          item.rincian_penerima.filter(
                                                            (p) =>
                                                              p.asal_perintah_dari_id !==
                                                              userId
                                                          ).length -
                                                            1
                                                            ? "1px solid #e8e8e8"
                                                            : "none",
                                                        p: 2,
                                                        background: "#ffffff",
                                                      }}
                                                    >
                                                      {/* Asal Perintah (Pemberi Instruksi Lanjutan) */}
                                                      {penerima.asal_perintah_nama && (
                                                        <MDBox
                                                          mb={1.5}
                                                          sx={{
                                                            p: 1,
                                                            background:
                                                              "#f5f5f5",
                                                            borderRadius: 1,
                                                            border:
                                                              "1px solid #e0e0e0",
                                                          }}
                                                        >
                                                          <MDBox
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                            mb={0.5}
                                                          >
                                                            <ArrowForwardIcon
                                                              sx={{
                                                                fontSize: 16,
                                                                color:
                                                                  "text.secondary",
                                                              }}
                                                            />
                                                            <MDTypography
                                                              variant="caption"
                                                              color="text.secondary"
                                                              fontWeight="600"
                                                              sx={{
                                                                fontSize:
                                                                  "0.65rem",
                                                              }}
                                                            >
                                                              Dari:
                                                            </MDTypography>
                                                          </MDBox>
                                                          <MDBox
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                          >
                                                            <MDAvatar
                                                              src={
                                                                penerima
                                                                  .instruksi
                                                                  ?.asal_perintah_dari
                                                                  ?.uid_RSS ||
                                                                penerima.asal_perintah_uid_RSS
                                                                  ? `https://smartdh.rotendaokab.go.id/uploads/${
                                                                      penerima
                                                                        .instruksi
                                                                        ?.asal_perintah_dari
                                                                        ?.uid_RSS ||
                                                                      penerima.asal_perintah_uid_RSS
                                                                    }.jpg`
                                                                  : undefined
                                                              }
                                                              alt={
                                                                penerima.asal_perintah_nama
                                                              }
                                                              size="xs"
                                                            />
                                                            <MDTypography
                                                              variant="caption"
                                                              color="dark"
                                                              fontWeight="600"
                                                              sx={{
                                                                fontSize:
                                                                  "0.7rem",
                                                              }}
                                                            >
                                                              {
                                                                penerima.asal_perintah_nama
                                                              }
                                                            </MDTypography>
                                                          </MDBox>
                                                        </MDBox>
                                                      )}

                                                      {/* Penerima Instruksi */}
                                                      <MDBox
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1.5}
                                                      >
                                                        <MDAvatar
                                                          src={
                                                            penerima.instruksi
                                                              ?.penerima
                                                              ?.uid_RSS ||
                                                            penerima.uid_RSS
                                                              ? `https://smartdh.rotendaokab.go.id/uploads/${
                                                                  penerima
                                                                    .instruksi
                                                                    ?.penerima
                                                                    ?.uid_RSS ||
                                                                  penerima.uid_RSS
                                                                }.jpg`
                                                              : undefined
                                                          }
                                                          size="sm"
                                                          sx={{
                                                            border:
                                                              "2px solid #ffffff",
                                                            boxShadow:
                                                              "0 1px 3px rgba(0,0,0,0.1)",
                                                          }}
                                                        />
                                                        <MDBox>
                                                          <MDTypography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            fontWeight="600"
                                                            sx={{
                                                              fontSize:
                                                                "0.65rem",
                                                              mb: 0.25,
                                                            }}
                                                          >
                                                            Penerima:
                                                          </MDTypography>
                                                          <MDTypography
                                                            variant="button"
                                                            fontWeight="600"
                                                            color="dark"
                                                            sx={{
                                                              fontSize:
                                                                "0.8rem",
                                                            }}
                                                          >
                                                            {penerima.nama ||
                                                              "-"}
                                                          </MDTypography>
                                                          <MDBox
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                            mt={0.5}
                                                          >
                                                            {statusDisplay.icon}
                                                            <MDTypography
                                                              variant="caption"
                                                              color={
                                                                statusDisplay.color
                                                              }
                                                              fontWeight="600"
                                                              sx={{
                                                                fontSize:
                                                                  "0.65rem",
                                                              }}
                                                            >
                                                              {
                                                                statusDisplay.label
                                                              }
                                                            </MDTypography>
                                                          </MDBox>
                                                        </MDBox>
                                                      </MDBox>

                                                      {/* Tingkat Perintah */}
                                                      {penerima.tingkat && (
                                                        <MDBox
                                                          mt={1}
                                                          sx={{
                                                            p: 0.75,
                                                            background:
                                                              "#fafbfc",
                                                            borderRadius: 1,
                                                            border:
                                                              "1px solid #e8e8e8",
                                                          }}
                                                        >
                                                          <MDTypography
                                                            variant="caption"
                                                            color="text"
                                                            fontWeight="600"
                                                            sx={{
                                                              fontSize:
                                                                "0.65rem",
                                                            }}
                                                          >
                                                            Tingkat: Level{" "}
                                                            {penerima.tingkat}
                                                          </MDTypography>
                                                        </MDBox>
                                                      )}

                                                      {/* Waktu Diterima */}
                                                      {penerima.waktu_diterima && (
                                                        <MDBox
                                                          mt={1}
                                                          sx={{
                                                            p: 0.75,
                                                            background:
                                                              "#fafbfc",
                                                            borderRadius: 1,
                                                            border:
                                                              "1px solid #e8e8e8",
                                                          }}
                                                        >
                                                          <MDTypography
                                                            variant="caption"
                                                            color="text"
                                                            fontWeight="600"
                                                            sx={{
                                                              fontSize:
                                                                "0.65rem",
                                                            }}
                                                          >
                                                            Diterima:{" "}
                                                            {formatDateTime(
                                                              penerima.waktu_diterima
                                                            )}
                                                          </MDTypography>
                                                        </MDBox>
                                                      )}

                                                      {/* Waktu Selesai */}
                                                      {statusDisplay.label ===
                                                        "Selesai" &&
                                                        penerima.waktu_selesai && (
                                                          <MDBox
                                                            mt={1}
                                                            sx={{
                                                              p: 0.75,
                                                              background:
                                                                "#fafbfc",
                                                              borderRadius: 1,
                                                              border:
                                                                "1px solid #e8e8e8",
                                                            }}
                                                          >
                                                            <MDTypography
                                                              variant="caption"
                                                              color="text"
                                                              fontWeight="600"
                                                              sx={{
                                                                fontSize:
                                                                  "0.65rem",
                                                              }}
                                                            >
                                                              Selesai:{" "}
                                                              {formatDateTime(
                                                                penerima.waktu_selesai
                                                              )}
                                                            </MDTypography>
                                                          </MDBox>
                                                        )}
                                                    </MDBox>
                                                  );
                                                })}
                                            </MDBox>
                                          </MDBox>
                                        )}
                                      </MDBox>
                                    )}
                                </MDBox>
                              </Card>
                            );
                          })}
                          {instruksiOleh.length === 0 && !loading && (
                            <MDBox
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              py={4}
                            >
                              <MDTypography variant="h6" color="text.secondary">
                                Tidak ada data instruksi
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                      ) : (
                        <DataTable
                          table={{
                            columns: [
                              {
                                Header: "No",
                                accessor: "no",
                                width: "5%",
                                Cell: ({ row }) => (
                                  <MDTypography variant="caption" color="text">
                                    {row.index + 1}
                                  </MDTypography>
                                ),
                              },
                              {
                                Header: "Pembuat Instruksi",
                                accessor: "pengirim",
                                width: "12%",
                                Cell: ({ row }) => (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <MDAvatar
                                      src={
                                        row.original.pengirim?.uid_RSS ||
                                        row.original.instruksi_asal?.pengirim
                                          ?.uid_RSS
                                          ? `https://smartdh.rotendaokab.go.id/uploads/${
                                              row.original.pengirim?.uid_RSS ||
                                              row.original.instruksi_asal
                                                ?.pengirim?.uid_RSS
                                            }.jpg`
                                          : undefined
                                      }
                                      size="xs"
                                    />
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                    >
                                      {row.original.pengirim?.nama || "-"}
                                    </MDTypography>
                                  </MDBox>
                                ),
                              },
                              {
                                Header: "Perihal & Instruksi",
                                accessor: "perihal_instruksi",
                                width: "25%",
                                Cell: ({ row }) => {
                                  const stripHtml = (html) => {
                                    if (!html) return "-";
                                    const tmp = document.createElement("DIV");
                                    tmp.innerHTML = html;
                                    const text =
                                      tmp.textContent || tmp.innerText || "";
                                    return text;
                                  };

                                  const perihal = stripHtml(
                                    row.original.perihal || "-"
                                  );
                                  const isiInstruksi = stripHtml(
                                    row.original.isi_instruksi || "-"
                                  );
                                  const prioritas =
                                    row.original.prioritas || "-";

                                  const perihalPreview =
                                    perihal.length > 40
                                      ? perihal.substring(0, 40) + "..."
                                      : perihal;
                                  const isiPreview =
                                    isiInstruksi.length > 40
                                      ? isiInstruksi.substring(0, 40) + "..."
                                      : isiInstruksi;

                                  return (
                                    <MDBox
                                      sx={{
                                        whiteSpace: "normal",
                                        wordWrap: "break-word",
                                        py: 0.5,
                                      }}
                                    >
                                      <MDBox
                                        onClick={() =>
                                          handleOpenPerihalDialog(
                                            row.original.perihal || "-"
                                          )
                                        }
                                        sx={{
                                          cursor: "pointer",
                                          mb: 0.5,
                                          "&:hover": {
                                            textDecoration: "underline",
                                            color: "primary.main",
                                          },
                                        }}
                                      >
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          fontWeight="bold"
                                        >
                                          {perihalPreview}
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox
                                        onClick={() =>
                                          handleOpenIsiInstruksiDialog(
                                            row.original.isi_instruksi || "-"
                                          )
                                        }
                                        sx={{
                                          cursor: "pointer",
                                          mb: 0.5,
                                          "&:hover": {
                                            textDecoration: "underline",
                                            color: "primary.main",
                                          },
                                        }}
                                      >
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {isiPreview}
                                        </MDTypography>
                                      </MDBox>
                                      <Chip
                                        label={prioritas}
                                        size="small"
                                        sx={{
                                          backgroundColor:
                                            PRIORITAS_COLOR[prioritas] ||
                                            "#f5f5f5",
                                          color: "white",
                                          fontWeight: 600,
                                          fontSize: "0.65rem",
                                          height: "20px",
                                          mt: 0.5,
                                        }}
                                      />
                                    </MDBox>
                                  );
                                },
                              },
                              {
                                Header: "Tanggal Mulai & Finish",
                                accessor: "tanggal_range",
                                width: "15%",
                                Cell: ({ row }) => (
                                  <MDBox
                                    sx={{
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    <MDBox mb={0.5}>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Mulai:
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text"
                                      >
                                        {formatDateTime(row.original.tanggal)}
                                      </MDTypography>
                                    </MDBox>
                                    <MDBox>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        Finish:
                                      </MDTypography>
                                      <MDTypography
                                        variant="caption"
                                        color="text"
                                      >
                                        {formatDateTime(
                                          row.original.tanggal_selesai
                                        )}
                                      </MDTypography>
                                    </MDBox>
                                  </MDBox>
                                ),
                              },
                              {
                                Header: "Penerima Langsung",
                                accessor: "penerima_langsung",
                                width: "12%",
                                Cell: ({ row }) =>
                                  renderPenerimaLangsung(
                                    row.original.rincian_penerima,
                                    row.original
                                  ),
                              },
                              {
                                Header: "Progress & Status",
                                accessor: "progress_status",
                                width: "15%",
                                Cell: ({ row }) => (
                                  <MDBox
                                    sx={{
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    <MDBox mb={1}>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mb: 0.5 }}
                                      >
                                        Progress:
                                      </MDTypography>
                                      {renderProgress(
                                        row.original.rincian_penerima,
                                        userId
                                      )}
                                    </MDBox>
                                    <MDBox>
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mb: 0.5 }}
                                      >
                                        Status:
                                      </MDTypography>
                                      {renderStatus(row.original, userId)}
                                    </MDBox>
                                  </MDBox>
                                ),
                              },
                              {
                                Header: "Dokumen",
                                accessor: "dokumen",
                                width: "8%",
                                Cell: ({ row }) =>
                                  renderDokumenIcon(row.original.dokumen),
                              },
                              {
                                Header: "Respon",
                                accessor: "respon",
                                width: "15%",
                                Cell: ({ row }) =>
                                  renderRespon(
                                    row.original,
                                    row.original.rincian_penerima,
                                    userId
                                  ),
                              },
                              {
                                Header: "Penerima Lanjutan",
                                accessor: "penerima_lanjutan",
                                width: "12%",
                                Cell: ({ row }) =>
                                  renderPenerimaLanjutan(
                                    row.original.rincian_penerima,
                                    row.original
                                  ),
                              },
                              {
                                Header: "Aksi",
                                accessor: "aksi",
                                width: "8%",
                                Cell: ({ row }) => {
                                  const item = row.original;
                                  const selesai = isInstruksiSelesai(
                                    item,
                                    userId
                                  );
                                  return (
                                    <MDBox display="flex" gap={1}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={selesai}
                                        onClick={() =>
                                          handleOpenEditDialog(item)
                                        }
                                        title="Edit Instruksi"
                                        sx={{
                                          opacity: selesai ? 0.5 : 1,
                                          cursor: selesai
                                            ? "not-allowed"
                                            : "pointer",
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={selesai}
                                        onClick={() =>
                                          handleOpenDeleteConfirm(item)
                                        }
                                        title="Hapus Instruksi"
                                        sx={{
                                          opacity: selesai ? 0.5 : 1,
                                          cursor: selesai
                                            ? "not-allowed"
                                            : "pointer",
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </MDBox>
                                  );
                                },
                              },
                            ],
                            rows: tableData,
                          }}
                          entriesPerPage={{
                            defaultValue: 10,
                            entries: [5, 10, 15, 20, 25],
                          }}
                          showTotalEntries={true}
                          isSorted={true}
                          noEndBorder
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      {/* Modal Form Lanjutkan/Selesaikan */}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalType === "lanjutkan"
            ? "Lanjutkan Instruksi ke Level Bawah"
            : "Selesaikan Instruksi"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={
              modalType === "lanjutkan"
                ? "Catatan/Instruksi"
                : "Catatan Penyelesaian/Laporan"
            }
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            value={modalValues.catatan}
            onChange={(e) => handleModalInput("catatan", e.target.value)}
          />
          <MDBox mt={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDBox my={2} display="inline-block">
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                  >
                    Tags
                  </MDTypography>
                </MDBox>
                {/* Autocomplete hanya tampil jika modalType !== 'selesaikan' */}
                {modalType !== "selesaikan" && (
                  <Autocomplete
                    multiple
                    options={allOptions}
                    value={penerima.penerima || []}
                    onChange={handleChange}
                    getOptionLabel={(option) => option.label}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        {option.value === "all" ? (
                          <strong style={{ color: "#1976d2" }}>
                            Pilih Semua
                          </strong>
                        ) : (
                          <>
                            <MDAvatar
                              src={option.avatar}
                              alt={option.label}
                              size="xs"
                              sx={{ mr: 1 }}
                            />
                            {option.label}
                          </>
                        )}
                      </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                      selected.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          avatar={
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <MDAvatar
                                src={option.avatar}
                                alt={option.label}
                                size="xxs"
                                sx={{ width: 24, height: 24, mr: 1 }}
                              />
                              <span style={{ marginLeft: 4 }} />
                            </span>
                          }
                          label={option.label}
                          style={{
                            marginRight: 4,
                            marginBottom: 2,
                            display: "flex",
                            alignItems: "center",
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        variant="standard"
                        label="Penerima Instruksi"
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
          </MDBox>

          <MDBox mt={2} mb={1}>
            <MDBox mb={2} display="inline-block">
              <MDTypography
                component="label"
                variant="button"
                fontWeight="regular"
                color="text"
                textTransform="capitalize"
              >
                Status Pelaksana
              </MDTypography>
            </MDBox>
            <Autocomplete
              value={modalValues.status_pelaksana || ""}
              onChange={(e, value) =>
                handleModalInput("status_pelaksana", value)
              }
              options={["belum", "proses", "selesai"]}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : String(option)
              }
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  label="Pilih Status Pelaksana"
                />
              )}
            />
          </MDBox>

          {/* Input status, waktu instruksi, waktu selesai hanya tampil jika bukan selesaikan */}
          {modalType !== "selesaikan" && (
            <TextField
              label="Waktu Instruksi"
              type="datetime-local"
              fullWidth
              sx={{ mt: 2 }}
              value={modalValues.waktu_diterima}
              onChange={(e) =>
                handleModalInput("waktu_diterima", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="Waktu Selesai"
            type="datetime-local"
            fullWidth
            sx={{ mt: 2 }}
            value={modalValues.waktu_selesai}
            onChange={(e) => handleModalInput("waktu_selesai", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <MDBox mt={2}>
            <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <MDTypography
                component="label"
                variant="button"
                fontWeight="regular"
                color="text"
              >
                Upload dokumen, jika ada (bisa lebih dari satu file)
                <br />
                <MDTypography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  Format: PDF, Word, Excel, JPG | Maksimal total: 3 MB untuk
                  semua file
                </MDTypography>
              </MDTypography>
            </MDBox>
            {useMemo(
              () => (
                <MDDropzone
                  options={{
                    addRemoveLinks: true,
                    uploadMultiple: true,
                    maxTotalFilesize: 3, // Maksimal 3 MB total untuk semua file
                    acceptedFiles:
                      ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg", // PDF, Word, Excel, JPG
                    dictDefaultMessage:
                      "Klik atau seret file ke sini untuk mengunggah",
                    dictTotalFilesTooBig:
                      "Total ukuran semua file melebihi batas maksimal 3 MB",
                    dictInvalidFileType:
                      "Format file tidak didukung. Hanya PDF, Word (DOC/DOCX), Excel (XLS/XLSX), dan JPG yang diperbolehkan",
                    dictResponseError: "Terjadi kesalahan saat mengunggah file",
                    dictCancelUpload: "Batalkan unggah",
                    dictUploadCanceled: "Unggah dibatalkan",
                    dictCancelUploadConfirmation:
                      "Apakah Anda yakin ingin membatalkan unggah?",
                    dictRemoveFile: "Hapus file",
                  }}
                  onChange={handleDropzoneChange}
                  ref={dropzoneRef}
                />
              ),
              []
            )}
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text.secondary">
                * Format yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX),
                JPG
                <br />* Maksimal total ukuran semua file: 3 MB
              </MDTypography>
            </MDBox>
          </MDBox>
          {/* Preview file yang sudah diupload (jika ingin manual): */}
          {modalValues.files &&
            modalValues.files.length > 0 &&
            (() => {
              // Hitung total ukuran
              const totalSize = modalValues.files.reduce((sum, file) => {
                const fileObj = file.file || file;
                return sum + (fileObj.size || 0);
              }, 0);
              const totalSizeMB = totalSize / (1024 * 1024);
              const maxSizeMB = 3;
              const isExceeding = totalSizeMB > maxSizeMB;

              return (
                <MDBox mt={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      color="text"
                    >
                      File terupload:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color={isExceeding ? "error" : "text.secondary"}
                      fontWeight="medium"
                    >
                      Total: {totalSizeMB.toFixed(2)} MB / {maxSizeMB} MB
                    </MDTypography>
                  </MDBox>
                  <ul style={{ paddingLeft: 16 }}>
                    {modalValues.files.map((file, idx) => {
                      const fileObj = file.file || file;
                      return (
                        <li key={idx} style={{ fontSize: 13 }}>
                          {fileObj.name || "Unknown"} (
                          {fileObj.size
                            ? `${(fileObj.size / 1024).toFixed(2)} KB`
                            : "-"}
                          )
                        </li>
                      );
                    })}
                  </ul>
                </MDBox>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseModal}
            color="secondary"
            disabled={loadingModal}
          >
            Batal
          </Button>
          <Button
            onClick={handleSubmitModal}
            color="primary"
            variant="contained"
            disabled={loadingModal}
          >
            {loadingModal ? "Mengirim..." : "Kirim"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Delete */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Konfirmasi Hapus</DialogTitle>
        <DialogContent>
          <MDTypography>
            Apakah Anda yakin ingin menghapus instruksi yang
            <bb>
              {deleteTarget && deleteTarget.beri_instruksi
                ? " diberikan "
                : " diteruskan "}
            </bb>
            ke
            <b> {deleteTarget?.nama_penerima || "-"} </b>?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
            Batal
          </Button>
          <Button onClick={handleDeleteChild} color="error" variant="contained">
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Delete Instruksi Utama */}
      <Dialog
        open={openDeleteInstruksiDialog}
        onClose={() => setOpenDeleteInstruksiDialog(false)}
      >
        <DialogTitle>Konfirmasi Hapus Instruksi</DialogTitle>
        <DialogContent>
          <MDTypography>
            Apakah Anda yakin ingin menghapus instruksi utama ini beserta
            seluruh penerimanya?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteInstruksiDialog(false)}
            color="secondary"
          >
            Batal
          </Button>
          <Button
            onClick={handleDeleteInstruksi}
            color="error"
            variant="contained"
          >
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Status Instruksi */}
      <Dialog
        open={openStatusDialog}
        onClose={handleCloseStatusDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <MDTypography variant="h6" fontWeight="bold" color="dark">
              {(() => {
                switch (selectedStatus) {
                  case "selesai":
                    return "Instruksi Selesai";
                  case "dalamproses":
                    return "Instruksi Dalam Proses";
                  case "belumdimulai":
                    return "Instruksi Belum Dimulai";
                  case "lewatdeadline":
                    return "Instruksi Lewat Deadline";
                  default:
                    return "Daftar Instruksi";
                }
              })()}
            </MDTypography>
            <IconButton onClick={handleCloseStatusDialog} size="small">
              <CancelIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            {filteredInstruksi.length === 0 ? (
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={4}
              >
                <Icon sx={{ fontSize: 64, color: "#ccc", mb: 2 }}>
                  assignment
                </Icon>
                <MDTypography variant="h6" color="text" textAlign="center">
                  Tidak ada instruksi dengan status ini
                </MDTypography>
                <MDTypography variant="body2" color="text" textAlign="center">
                  Semua instruksi sudah diproses atau belum ada data
                </MDTypography>
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                {filteredInstruksi.map((item) => {
                  // Hitung progres
                  const totalPenerima = Array.isArray(item.rincian_penerima)
                    ? item.rincian_penerima.length
                    : 0;
                  const selesaiCount = Array.isArray(item.rincian_penerima)
                    ? item.rincian_penerima.filter(
                        (p) => (p.status || "").toLowerCase() === "selesai"
                      ).length
                    : 0;
                  const progress =
                    totalPenerima > 0
                      ? Math.round((selesaiCount / totalPenerima) * 100)
                      : 0;

                  // Status label dan color
                  let statusLabel = "Belum dikerjakan";
                  let statusColor = "#bdbdbd";
                  if (selesaiCount === 0) {
                    statusLabel = "Belum dikerjakan";
                    statusColor = "#bdbdbd";
                  } else if (selesaiCount > 0 && selesaiCount < totalPenerima) {
                    statusLabel = "On Progress";
                    statusColor = "#ffb300";
                  } else if (
                    selesaiCount === totalPenerima &&
                    totalPenerima > 0
                  ) {
                    statusLabel = "Completed";
                    statusColor = "#4caf50";
                  }

                  // Sisa waktu
                  const today = new Date();
                  const tglSelesai = item.tanggal_selesai
                    ? new Date(item.tanggal_selesai)
                    : null;
                  let sisaWaktuLabel = "";
                  if (tglSelesai) {
                    const diffTime =
                      tglSelesai.setHours(0, 0, 0, 0) -
                      today.setHours(0, 0, 0, 0);
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    if (diffDays > 0) {
                      sisaWaktuLabel = `Sisa waktu: ${diffDays} hari`;
                    } else if (diffDays === 0) {
                      sisaWaktuLabel = "Deadline hari ini";
                    } else {
                      sisaWaktuLabel = "Lewat deadline";
                    }
                  }

                  // Dokumen
                  let dokumenArr = [];
                  if (Array.isArray(item.dokumen)) {
                    dokumenArr = item.dokumen;
                  } else if (
                    typeof item.dokumen === "string" &&
                    item.dokumen.trim().startsWith("[")
                  ) {
                    try {
                      dokumenArr = JSON.parse(item.dokumen);
                    } catch (e) {
                      dokumenArr = [];
                    }
                  }

                  return (
                    <Grid item xs={12} key={item.instruksi_id}>
                      <Card
                        sx={{
                          p: 2,
                          width: "100%",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          borderRadius: 2,
                          border: "1px solid #e8e8e8",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {/* Header Card */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <MDBox display="flex" alignItems="center">
                            <MDAvatar
                              src={
                                item.pengirim?.uid_RSS
                                  ? `https://smartdh.rotendaokab.go.id/uploads/${item.pengirim.uid_RSS}.jpg`
                                  : undefined
                              }
                              size="sm"
                              sx={{ mr: 1.5 }}
                            />
                            <MDBox>
                              <MDTypography
                                variant="button"
                                fontWeight="600"
                                color="dark"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {item.pengirim?.nama || "-"}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {formatDate(item.tanggal)}
                              </MDTypography>
                            </MDBox>
                          </MDBox>

                          <MDBox display="flex" alignItems="center" gap={1}>
                            {sisaWaktuLabel && (
                              <Chip
                                label={sisaWaktuLabel}
                                size="small"
                                color={
                                  sisaWaktuLabel === "Lewat deadline"
                                    ? "error"
                                    : sisaWaktuLabel === "Deadline hari ini"
                                    ? "warning"
                                    : "success"
                                }
                                sx={{ fontSize: "0.7rem" }}
                              />
                            )}
                            <Chip
                              label={item.prioritas}
                              size="small"
                              sx={{
                                background:
                                  PRIORITAS_COLOR[item.prioritas] || "#f5f5f5",
                                color: "white",
                                fontSize: "0.7rem",
                              }}
                            />
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{
                                background: statusColor,
                                color: "white",
                                fontSize: "0.7rem",
                              }}
                            />
                          </MDBox>
                        </MDBox>

                        {/* Progress Bar */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          mb={2}
                        >
                          <MDBox flex={1}>
                            <MDBox
                              sx={{
                                width: "100%",
                                background: "#f0f0f0",
                                borderRadius: 1,
                                height: 6,
                                overflow: "hidden",
                              }}
                            >
                              <MDBox
                                sx={{
                                  width: `${progress}%`,
                                  background:
                                    progress === 100
                                      ? "linear-gradient(90deg, #00c875, #00d884)"
                                      : "linear-gradient(90deg, #0073ea, #1a8cff)",
                                  height: "100%",
                                  borderRadius: 1,
                                }}
                              />
                            </MDBox>
                          </MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            sx={{ fontWeight: 600, minWidth: 32 }}
                          >
                            {progress}%
                          </MDTypography>
                        </MDBox>

                        {/* Content */}
                        {item.perihal && (
                          <MDTypography
                            variant="h6"
                            color="dark"
                            fontWeight="600"
                            sx={{ mb: 1.5, fontSize: "1rem" }}
                          >
                            {item.perihal}
                          </MDTypography>
                        )}

                        <MDBox
                          mb={2}
                          sx={{
                            background: "#fafbfc",
                            p: 1.5,
                            borderRadius: 1,
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: item.isi_instruksi || "",
                            }}
                            style={{
                              fontSize: "0.875rem",
                              color: "#333",
                              lineHeight: 1.5,
                              maxHeight: "100px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          />
                        </MDBox>

                        {/* Dokumen */}
                        {dokumenArr.length > 0 && (
                          <MDBox mb={2}>
                            <MDTypography
                              variant="button"
                              color="text"
                              fontWeight="600"
                              mb={1}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontSize: "0.8rem",
                              }}
                            >
                              <Icon sx={{ fontSize: 16 }}>attach_file</Icon>
                              Dokumen ({dokumenArr.length})
                            </MDTypography>
                            <MDBox display="flex" gap={1} flexWrap="wrap">
                              {dokumenArr.slice(0, 3).map((link, idx) => {
                                const filename = link.split("/").pop();
                                return (
                                  <Button
                                    key={idx}
                                    href={`${API_ESA_OFFICE_UPLOAD}${link}`}
                                    target="_blank"
                                    size="small"
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "0.7rem",
                                      minWidth: 60,
                                    }}
                                    variant="outlined"
                                    startIcon={getFileIcon(filename)}
                                  >
                                    Dok {idx + 1}
                                  </Button>
                                );
                              })}
                              {dokumenArr.length > 3 && (
                                <Chip
                                  label={`+${dokumenArr.length - 3} lagi`}
                                  size="small"
                                  sx={{ fontSize: "0.7rem" }}
                                />
                              )}
                            </MDBox>
                          </MDBox>
                        )}

                        {/* Penerima Summary - Tampilan Langsung */}
                        {Array.isArray(item.rincian_penerima) &&
                          item.rincian_penerima.length > 0 && (
                            <MDBox
                              sx={{
                                border: "1px solid #e8e8e8",
                                borderRadius: 1.5,
                                overflow: "hidden",
                                background: "#ffffff",
                              }}
                            >
                              {/* Header Penerima */}
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                p={2}
                                sx={{
                                  background: "#fafbfc",
                                  borderBottom: "1px solid #e8e8e8",
                                }}
                              >
                                <MDBox display="flex" alignItems="center">
                                  <MDTypography
                                    variant="button"
                                    color="text"
                                    fontWeight="600"
                                    mr={1.5}
                                    sx={{ fontSize: "0.8rem" }}
                                  >
                                    <Icon sx={{ fontSize: 16, mr: 0.5 }}>
                                      people
                                    </Icon>
                                    Penerima ({totalPenerima})
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center">
                                    {(Array.isArray(item.rincian_penerima)
                                      ? item.rincian_penerima.slice(0, 3)
                                      : []
                                    ).map((penerima, idx) => (
                                      <MDAvatar
                                        key={penerima.user_id}
                                        src={
                                          penerima.uid_RSS
                                            ? `https://smartdh.rotendaokab.go.id/uploads/${penerima.uid_RSS}.jpg`
                                            : undefined
                                        }
                                        size="xs"
                                        sx={{
                                          ml: idx === 0 ? 0 : -0.5,
                                          border: "2px solid #ffffff",
                                        }}
                                      />
                                    ))}
                                    {Array.isArray(item.rincian_penerima) &&
                                      item.rincian_penerima.length > 3 && (
                                        <MDTypography
                                          variant="caption"
                                          color="text"
                                          sx={{ ml: 1, fontWeight: 600 }}
                                        >
                                          +{item.rincian_penerima.length - 3}
                                        </MDTypography>
                                      )}
                                  </MDBox>
                                </MDBox>

                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {selesaiCount}/{totalPenerima} selesai
                                </MDTypography>
                              </MDBox>

                              {/* List Penerima */}
                              <MDBox
                                sx={{
                                  background: "#fafbfc",
                                }}
                              >
                                {item.rincian_penerima.map((penerima, idx) => {
                                  // Status dengan icon
                                  const getStatusDisplay = () => {
                                    const status = (
                                      penerima && penerima.status
                                        ? penerima.status
                                        : "-"
                                    ).toLowerCase();
                                    if (status === "selesai") {
                                      return {
                                        icon: (
                                          <CheckCircleIcon
                                            sx={{
                                              color: "#00c875",
                                              fontSize: 16,
                                            }}
                                          />
                                        ),
                                        label: "Selesai",
                                        color: "success",
                                      };
                                    } else if (status === "belum") {
                                      return {
                                        icon: (
                                          <CancelIcon
                                            sx={{
                                              color: "#ff5c5c",
                                              fontSize: 16,
                                            }}
                                          />
                                        ),
                                        label: "Belum",
                                        color: "error",
                                      };
                                    } else if (
                                      status === "proses" ||
                                      status === "on progress"
                                    ) {
                                      return {
                                        icon: (
                                          <HourglassBottomIcon
                                            sx={{
                                              color: "#ffb300",
                                              fontSize: 16,
                                            }}
                                          />
                                        ),
                                        label: "Proses",
                                        color: "warning",
                                      };
                                    } else {
                                      return {
                                        icon: (
                                          <Icon
                                            sx={{
                                              color: "#666",
                                              fontSize: 16,
                                            }}
                                          >
                                            help
                                          </Icon>
                                        ),
                                        label: status,
                                        color: "text",
                                      };
                                    }
                                  };

                                  const statusDisplay = getStatusDisplay();

                                  // File tindak lanjut
                                  let files = [];
                                  if (Array.isArray(penerima.file)) {
                                    files = penerima.file;
                                  } else if (
                                    typeof penerima.file === "string" &&
                                    penerima.file.trim().startsWith("[")
                                  ) {
                                    try {
                                      files = JSON.parse(penerima.file);
                                    } catch (e) {
                                      files = [];
                                    }
                                  }

                                  return (
                                    <MDBox
                                      key={`penerima-${item.instruksi_id}-${
                                        penerima.user_id || idx
                                      }`}
                                      sx={{
                                        borderBottom:
                                          idx < item.rincian_penerima.length - 1
                                            ? "1px solid #e8e8e8"
                                            : "none",
                                        p: 2,
                                        background: "#ffffff",
                                      }}
                                    >
                                      <Grid container spacing={2}>
                                        {/* Info Penerima */}
                                        <Grid item xs={12} md={4}>
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={1.5}
                                          >
                                            <MDAvatar
                                              src={
                                                penerima.uid_RSS
                                                  ? `https://smartdh.rotendaokab.go.id/uploads/${penerima.uid_RSS}.jpg`
                                                  : undefined
                                              }
                                              size="sm"
                                              sx={{
                                                border: "2px solid #ffffff",
                                                boxShadow:
                                                  "0 1px 3px rgba(0,0,0,0.1)",
                                              }}
                                            />
                                            <MDBox>
                                              <MDTypography
                                                variant="button"
                                                fontWeight="600"
                                                color="dark"
                                                sx={{ fontSize: "0.8rem" }}
                                              >
                                                {penerima.nama || "-"}
                                              </MDTypography>
                                              <MDBox
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                              >
                                                {statusDisplay.icon}
                                                <MDTypography
                                                  variant="caption"
                                                  color={statusDisplay.color}
                                                  fontWeight="600"
                                                  sx={{ fontSize: "0.65rem" }}
                                                >
                                                  {statusDisplay.label}
                                                </MDTypography>
                                              </MDBox>
                                            </MDBox>
                                          </MDBox>
                                        </Grid>

                                        {/* Detail Info */}
                                        <Grid item xs={12} md={4}>
                                          <MDBox
                                            display="flex"
                                            flexDirection="column"
                                            gap={0.5}
                                          >
                                            <MDBox
                                              display="flex"
                                              alignItems="center"
                                              gap={1}
                                              sx={{
                                                p: 0.5,
                                                background: "#fafbfc",
                                                borderRadius: 1,
                                                border: "1px solid #e8e8e8",
                                              }}
                                            >
                                              <MDTypography
                                                variant="caption"
                                                color="text"
                                                fontWeight="600"
                                                sx={{ fontSize: "0.6rem" }}
                                              >
                                                Tingkat:
                                              </MDTypography>
                                              <MDTypography
                                                variant="caption"
                                                color="dark"
                                                fontWeight="600"
                                                sx={{ fontSize: "0.6rem" }}
                                              >
                                                {penerima && penerima.tingkat
                                                  ? penerima.tingkat
                                                  : "-"}
                                              </MDTypography>
                                            </MDBox>

                                            {penerima.waktu_diterima && (
                                              <MDBox
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                  p: 0.5,
                                                  background: "#fafbfc",
                                                  borderRadius: 1,
                                                  border: "1px solid #e8e8e8",
                                                }}
                                              >
                                                <MDTypography
                                                  variant="caption"
                                                  color="text"
                                                  fontWeight="600"
                                                  sx={{ fontSize: "0.6rem" }}
                                                >
                                                  Diterima:
                                                </MDTypography>
                                                <MDTypography
                                                  variant="caption"
                                                  color="dark"
                                                  fontWeight="600"
                                                  sx={{ fontSize: "0.6rem" }}
                                                >
                                                  {formatDate(
                                                    penerima.waktu_diterima
                                                  )}
                                                </MDTypography>
                                              </MDBox>
                                            )}

                                            {penerima.waktu_selesai && (
                                              <MDBox
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                sx={{
                                                  p: 0.5,
                                                  background: "#fafbfc",
                                                  borderRadius: 1,
                                                  border: "1px solid #e8e8e8",
                                                }}
                                              >
                                                <MDTypography
                                                  variant="caption"
                                                  color="text"
                                                  fontWeight="600"
                                                  sx={{ fontSize: "0.6rem" }}
                                                >
                                                  Selesai:
                                                </MDTypography>
                                                <MDTypography
                                                  variant="caption"
                                                  color="dark"
                                                  fontWeight="600"
                                                  sx={{ fontSize: "0.6rem" }}
                                                >
                                                  {formatDate(
                                                    penerima.waktu_selesai
                                                  )}
                                                </MDTypography>
                                              </MDBox>
                                            )}
                                          </MDBox>
                                        </Grid>

                                        {/* File Tindak Lanjut */}
                                        <Grid item xs={12} md={4}>
                                          {files.length > 0 && (
                                            <MDBox>
                                              <MDTypography
                                                variant="caption"
                                                color="text"
                                                fontWeight="600"
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                  mb: 0.5,
                                                  fontSize: "0.6rem",
                                                }}
                                              >
                                                <Icon sx={{ fontSize: 10 }}>
                                                  attach_file
                                                </Icon>
                                                File ({files.length}):
                                              </MDTypography>
                                              <MDBox
                                                display="flex"
                                                flexDirection="column"
                                                gap={0.25}
                                              >
                                                {files
                                                  .slice(0, 2)
                                                  .map((file, fileIdx) => {
                                                    const filename = file
                                                      .split("/")
                                                      .pop();
                                                    return (
                                                      <Button
                                                        key={fileIdx}
                                                        onClick={() =>
                                                          handleOpenFile(file)
                                                        }
                                                        size="small"
                                                        sx={{
                                                          textTransform: "none",
                                                          justifyContent:
                                                            "flex-start",
                                                          fontSize: "0.6rem",
                                                          minWidth: "auto",
                                                          px: 0.75,
                                                          py: 0.25,
                                                          background: "#ffffff",
                                                          color: "#666",
                                                          border:
                                                            "1px solid #e0e0e0",
                                                          borderRadius: 0.5,
                                                          "&:hover": {
                                                            background:
                                                              "#f5f5f5",
                                                            borderColor:
                                                              "#0073ea",
                                                            color: "#0073ea",
                                                          },
                                                        }}
                                                        variant="outlined"
                                                        startIcon={getFileIcon(
                                                          filename
                                                        )}
                                                      >
                                                        {filename.length > 20
                                                          ? filename.substring(
                                                              0,
                                                              20
                                                            ) + "..."
                                                          : filename}
                                                      </Button>
                                                    );
                                                  })}
                                                {files.length > 2 && (
                                                  <MDTypography
                                                    variant="caption"
                                                    color="text"
                                                    sx={{
                                                      fontSize: "0.55rem",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    +{files.length - 2} file
                                                    lagi
                                                  </MDTypography>
                                                )}
                                              </MDBox>
                                            </MDBox>
                                          )}
                                        </Grid>
                                      </Grid>

                                      {/* Catatan Tindak Lanjut */}
                                      {penerima.instruksi_tindak_lanjut && (
                                        <MDBox
                                          mt={1.5}
                                          sx={{
                                            p: 1,
                                            background: "#f8f9fa",
                                            borderRadius: 1,
                                            border: "1px solid #e9ecef",
                                          }}
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="text"
                                            fontWeight="600"
                                            sx={{
                                              display: "block",
                                              mb: 0.5,
                                              fontSize: "0.6rem",
                                            }}
                                          >
                                            Catatan:
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            color="dark"
                                            sx={{
                                              fontSize: "0.65rem",
                                              lineHeight: 1.4,
                                              display: "-webkit-box",
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: "vertical",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {penerima.instruksi_tindak_lanjut}
                                          </MDTypography>
                                        </MDBox>
                                      )}
                                    </MDBox>
                                  );
                                })}
                              </MDBox>
                            </MDBox>
                          )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog} color="primary">
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Download File */}
      <Dialog
        open={openFileDialog}
        onClose={() => setOpenFileDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "#1976d2" }}>download</Icon>
            <MDTypography variant="h6" fontWeight="bold" color="dark">
              Download File
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {selectedFile && (
            <MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                gap={2}
                p={2}
                sx={{
                  background: "#f8f9fa",
                  borderRadius: 1,
                  border: "1px solid #e9ecef",
                  mb: 2,
                }}
              >
                {getFileIcon(selectedFile.name)}
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="600"
                    color="dark"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {selectedFile.name}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    File type: {selectedFile.type.toUpperCase()}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDTypography
                variant="body2"
                color="text"
                sx={{ lineHeight: 1.5 }}
              >
                File ini akan diunduh ke perangkat Anda. Apakah Anda yakin ingin
                melanjutkan?
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFileDialog(false)} color="secondary">
            Batal
          </Button>
          <Button
            onClick={handleDownloadFile}
            color="primary"
            variant="contained"
            startIcon={<Icon>download</Icon>}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan daftar penerima dengan Card */}
      <Dialog
        open={openPenerimaDialog}
        onClose={handleClosePenerimaDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            {penerimaFilterType === "langsung"
              ? "Daftar Penerima Langsung"
              : penerimaFilterType === "lanjutan"
              ? "Daftar Penerima Lanjutan"
              : "Daftar Penerima Instruksi"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            {selectedPenerima && selectedPenerima.length > 0 && (
              <>
                {/* Penerima Langsung - hanya tampil jika filterType null atau 'langsung' */}
                {(penerimaFilterType === null ||
                  penerimaFilterType === "langsung") &&
                  selectedPenerima.filter(
                    (p) => p.asal_perintah_dari_id === userId
                  ).length > 0 && (
                    <MDBox mb={penerimaFilterType === null ? 3 : 0}>
                      {penerimaFilterType === null && (
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          color="primary"
                          mb={2}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Icon>person</Icon>
                          Penerima Langsung (
                          {
                            selectedPenerima.filter(
                              (p) => p.asal_perintah_dari_id === userId
                            ).length
                          }
                          )
                        </MDTypography>
                      )}
                      <Grid container spacing={2}>
                        {selectedPenerima
                          .filter((p) => p.asal_perintah_dari_id === userId)
                          .map((penerima, idx) => {
                            return (
                              <Grid
                                item
                                xs={12}
                                key={
                                  penerima.user_id ||
                                  penerima.penerima_id ||
                                  `penerima-langsung-${idx}`
                                }
                              >
                                {renderPenerimaCard(
                                  penerima,
                                  selectedInstruksiForDialog
                                )}
                              </Grid>
                            );
                          })}
                      </Grid>
                    </MDBox>
                  )}

                {/* Penerima Lanjutan - hanya tampil jika filterType null atau 'lanjutan' */}
                {(penerimaFilterType === null ||
                  penerimaFilterType === "lanjutan") &&
                  selectedPenerima.filter(
                    (p) => p.asal_perintah_dari_id !== userId
                  ).length > 0 && (
                    <MDBox>
                      {penerimaFilterType === null && (
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          color="secondary"
                          mb={2}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Icon>people</Icon>
                          Penerima Lanjutan (
                          {
                            selectedPenerima.filter(
                              (p) => p.asal_perintah_dari_id !== userId
                            ).length
                          }
                          )
                        </MDTypography>
                      )}
                      <Grid container spacing={2}>
                        {selectedPenerima
                          .filter((p) => p.asal_perintah_dari_id !== userId)
                          .map((penerima, idx) => {
                            return (
                              <Grid
                                item
                                xs={12}
                                key={
                                  penerima.user_id ||
                                  penerima.penerima_id ||
                                  `penerima-lanjutan-${idx}`
                                }
                              >
                                {renderPenerimaCard(
                                  penerima,
                                  selectedInstruksiForDialog
                                )}
                              </Grid>
                            );
                          })}
                      </Grid>
                    </MDBox>
                  )}
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePenerimaDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan Isi Instruksi Lengkap */}
      <Dialog
        open={openIsiInstruksiDialog}
        onClose={handleCloseIsiInstruksiDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            Isi Instruksi Lengkap
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            {selectedIsiInstruksi ? (
              <MDBox
                sx={{
                  background: "#fafbfc",
                  p: 2,
                  borderRadius: 1.5,
                  border: "1px solid #e8e8e8",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedIsiInstruksi,
                  }}
                  style={{
                    fontSize: "0.875rem",
                    color: "#333",
                    wordBreak: "break-word",
                    lineHeight: 1.6,
                  }}
                />
              </MDBox>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                Tidak ada isi instruksi
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseIsiInstruksiDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog untuk menampilkan Perihal Lengkap */}
      <Dialog
        open={openPerihalDialog}
        onClose={handleClosePerihalDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            Perihal Instruksi
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2}>
            {selectedPerihal ? (
              <MDBox
                sx={{
                  background: "#fafbfc",
                  p: 2,
                  borderRadius: 1.5,
                  border: "1px solid #e8e8e8",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedPerihal,
                  }}
                  style={{
                    fontSize: "0.875rem",
                    color: "#333",
                    wordBreak: "break-word",
                    lineHeight: 1.6,
                  }}
                />
              </MDBox>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                Tidak ada perihal
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePerihalDialog}
            variant="contained"
            color="primary"
          >
            Tutup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Edit Instruksi */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            Edit Instruksi
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  label="Perihal"
                  fullWidth
                  value={editFormData.perihal}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      perihal: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput
                  label="Tanggal"
                  type="datetime-local"
                  fullWidth
                  value={editFormData.tanggal}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      tanggal: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput
                  label="Tanggal Selesai"
                  type="datetime-local"
                  fullWidth
                  value={editFormData.tanggal_selesai}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      tanggal_selesai: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={1}>
                  <MDTypography variant="button" fontWeight="medium">
                    Isi Instruksi
                  </MDTypography>
                </MDBox>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={editFormData.isi_instruksi}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      isi_instruksi: e.target.value,
                    })
                  }
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={["Rendah", "Sedang", "Tinggi"]}
                  value={editFormData.prioritas}
                  onChange={(event, newValue) =>
                    setEditFormData({ ...editFormData, prioritas: newValue })
                  }
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  renderInput={(params) => (
                    <MDInput {...params} label="Prioritas" variant="standard" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={1}>
                  <MDTypography variant="button" fontWeight="medium">
                    Penerima
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  options={penerimaOptions}
                  value={editFormData.penerima
                    .map((p) =>
                      penerimaOptions.find((opt) => opt.value === p.penerima_id)
                    )
                    .filter(Boolean)}
                  onChange={(event, newValue) => {
                    setEditFormData({
                      ...editFormData,
                      penerima: newValue.map((v) => ({
                        penerima_id: v.value,
                        asal_perintah_dari_id: userId,
                        tingkat_perintah: "1",
                      })),
                    });
                  }}
                  getOptionLabel={(option) => option.label}
                  renderOption={(props, option) => (
                    <MDBox
                      component="li"
                      {...props}
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      {option.avatar && (
                        <MDAvatar src={option.avatar} size="xs" />
                      )}
                      {option.label}
                    </MDBox>
                  )}
                  renderInput={(params) => (
                    <MDInput {...params} variant="standard" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mb={1}>
                  <MDTypography variant="button" fontWeight="medium">
                    Dokumen
                  </MDTypography>
                </MDBox>
                {/* Tampilkan dokumen lama jika ada */}
                {editFormData.dokumen && editFormData.dokumen.length > 0 && (
                  <MDBox mb={2}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      mb={1}
                    >
                      Dokumen yang sudah ada:
                    </MDTypography>
                    {editFormData.dokumen
                      .filter((d) => typeof d === "string")
                      .map((doc, idx) => (
                        <MDBox
                          key={idx}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={1}
                        >
                          <Icon>insert_drive_file</Icon>
                          <MDTypography variant="caption" color="text">
                            {doc.split("/").pop()}
                          </MDTypography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditFormData({
                                ...editFormData,
                                dokumen: editFormData.dokumen.filter(
                                  (d) => d !== doc
                                ),
                              });
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </MDBox>
                      ))}
                  </MDBox>
                )}
                <MDDropzone
                  options={{
                    addRemoveLinks: true,
                  }}
                  onChange={(files) => {
                    // Filter hanya file baru (File object)
                    const newFiles = files.filter((f) => f instanceof File);
                    setEditFormData({
                      ...editFormData,
                      dokumen: [
                        ...editFormData.dokumen.filter(
                          (d) => typeof d === "string"
                        ),
                        ...newFiles,
                      ],
                    });
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} variant="outlined">
            Batal
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            color="primary"
            disabled={loadingModal}
          >
            {loadingModal ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Delete */}
      <Dialog
        open={openDeleteConfirmDialog}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="error">
            Konfirmasi Hapus Instruksi
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2}>
            <MDTypography variant="body1" color="text">
              Apakah Anda yakin ingin menghapus instruksi ini? Tindakan ini
              tidak dapat dibatalkan.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteConfirm}
            variant="outlined"
            color="inherit"
          >
            Batal
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={loadingModal}
          >
            {loadingModal ? "Menghapus..." : "Hapus"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Chat - Material Dashboard Style */}
      <Dialog
        open={openChatDialog}
        onClose={handleCloseChat}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: "85vh",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* Header - Material Dashboard Style */}
        <MDBox
          sx={{
            bgcolor: "info.main",
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: 2,
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDAvatar
              src={
                selectedChatPenerima?.uid_RSS
                  ? `https://smartdh.rotendaokab.go.id/uploads/${selectedChatPenerima.uid_RSS}.jpg`
                  : undefined
              }
              alt={selectedChatPenerima?.nama}
              size="sm"
              sx={{ border: "2px solid rgba(255,255,255,0.3)" }}
            />
            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                {selectedChatPenerima?.nama || "-"}
              </MDTypography>
              <MDTypography variant="caption" color="white">
                {selectedChatInstruksi?.perihal || "Instruksi"}
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <IconButton
              onClick={handleRefreshChat}
              disabled={refreshingChat || loadingChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: refreshingChat ? "grey.500" : "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                  color: "grey.500",
                },
              }}
              title="Refresh Chat"
            >
              {refreshingChat ? (
                <CircularProgress size={20} sx={{ color: "grey.500" }} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <IconButton
              onClick={handleCloseChat}
              size="small"
              sx={{
                bgcolor: "white",
                color: "grey.700",
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              <CancelIcon />
            </IconButton>
          </MDBox>
        </MDBox>

        {/* Chat Messages Area - Material Dashboard Style */}
        <MDBox
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            bgcolor: "grey.100",
          }}
        >
          {loadingChat ? (
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <CircularProgress />
            </MDBox>
          ) : (
            <MDBox
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "rgba(0,0,0,0.3)",
                },
              }}
            >
              {chatMessages.length === 0 ? (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flex={1}
                >
                  <MDTypography variant="body2" color="text.secondary">
                    Belum ada pesan. Mulai percakapan!
                  </MDTypography>
                </MDBox>
              ) : (
                chatMessages.map((message, idx) => {
                  const currentUser =
                    JSON.parse(localStorage.getItem("user")) || {};
                  const currentUserId =
                    userId || currentUser.uid || currentUser.user_id;
                  const isOwnMessage = message.pengirim_id === currentUserId;
                  const isSending = message.isSending;

                  return (
                    <MDBox
                      key={message.id_chat || idx}
                      display="flex"
                      justifyContent={isOwnMessage ? "flex-end" : "flex-start"}
                      sx={{ mb: 0.5, px: 0.5 }}
                    >
                      <MDBox
                        sx={{
                          maxWidth: "75%",
                          minWidth: "100px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: isOwnMessage ? "flex-end" : "flex-start",
                        }}
                      >
                        {/* Avatar dan nama untuk pesan lawan */}
                        {!isOwnMessage && (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            mb={0.3}
                            sx={{ px: 0.5 }}
                          >
                            <MDAvatar
                              src={
                                message.pengirim_profil
                                  ? `https://smartdh.rotendaokab.go.id/uploads/${message.pengirim_profil}.jpg`
                                  : undefined
                              }
                              size="xs"
                            />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                            >
                              {message.pengirim_nama || "-"}
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* Bubble Chat */}
                        <MDBox
                          sx={{
                            p: 1.25,
                            px: 1.5,
                            borderRadius: isOwnMessage
                              ? "7.5px 0 7.5px 7.5px"
                              : "0 7.5px 7.5px 7.5px",
                            backgroundColor: isOwnMessage ? "#4caf50" : "white",
                            color: isOwnMessage ? "white" : "text.primary",
                            wordBreak: "break-word",
                            boxShadow: 1,
                            position: "relative",
                            opacity: isSending ? 0.7 : 1,
                            border: isOwnMessage ? "none" : "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDTypography
                            variant="body2"
                            sx={{
                              color: isOwnMessage ? "white" : "text.primary",
                              fontSize: "0.95rem",
                              lineHeight: 1.4,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {message.pesan}
                            {isSending && (
                              <MDBox
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  ml: 0.5,
                                  verticalAlign: "middle",
                                }}
                              >
                                <CircularProgress
                                  size={12}
                                  sx={{ color: "white" }}
                                />
                              </MDBox>
                            )}
                          </MDTypography>
                        </MDBox>

                        {/* Timestamp dan Read Status */}
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          sx={{ mt: 0.2, px: 0.5 }}
                        >
                          <MDTypography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              color: "#4caf50",
                              fontWeight: 400,
                            }}
                          >
                            {formatChatTime(message.waktu_kirim)}
                          </MDTypography>
                          {isOwnMessage && !isSending && (
                            <MDBox
                              sx={{
                                width: "12px",
                                height: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: "12px",
                                  color: message.waktu_baca
                                    ? "#4caf50"
                                    : "rgba(255,255,255,0.6)",
                                }}
                              />
                            </MDBox>
                          )}
                          {isOwnMessage && message.waktu_baca && (
                            <MDTypography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                color: "rgba(11, 183, 25, 0.7)",
                                fontStyle: "italic",
                              }}
                            >
                              Dibaca {formatChatTime(message.waktu_baca)}
                            </MDTypography>
                          )}
                          {/* Tombol Delete */}
                          {isOwnMessage && !isSending && (
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteMessage(message.id_chat)
                              }
                              sx={{
                                width: "20px",
                                height: "20px",
                                color: "rgba(255,255,255,0.7)",
                                "&:hover": {
                                  color: "white",
                                  backgroundColor: "rgba(255,255,255,0.1)",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: "14px" }} />
                            </IconButton>
                          )}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </MDBox>
          )}
        </MDBox>

        {/* Input Area - Material Dashboard Style */}
        <MDBox
          sx={{
            bgcolor: "grey.50",
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Ketik pesan..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            multiline
            maxRows={4}
            size="small"
            disabled={loadingChat}
            sx={{
              bgcolor: "white",
              borderRadius: "20px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                "& fieldset": {
                  borderColor: "rgba(0,0,0,0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0,0,0,0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loadingChat}
            sx={{
              bgcolor: "#000000",
              color: "white",
              width: "40px",
              height: "40px",
              "&:hover": {
                bgcolor: "#333333",
              },
              "&:disabled": {
                bgcolor: "rgba(0,0,0,0.1)",
                color: "rgba(0,0,0,0.3)",
              },
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </MDBox>
      </Dialog>

      {/* Dialog Respon Pemberi Instruksi */}
      <Dialog
        open={openResponDialog}
        onClose={handleCloseResponDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            {selectedPenerimaForRespon?.respon_pemberi_instruksi
              ? "Edit Respon"
              : "Berikan Respon"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          {selectedPenerimaForRespon && (
            <MDBox py={2}>
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={3}
              >
                <Avatar
                  src={
                    selectedPenerimaForRespon.uid_RSS ||
                    selectedPenerimaForRespon.instruksi?.penerima?.uid_RSS
                      ? `https://smartdh.rotendaokab.go.id/uploads/${
                          selectedPenerimaForRespon.uid_RSS ||
                          selectedPenerimaForRespon.instruksi?.penerima?.uid_RSS
                        }.jpg`
                      : undefined
                  }
                  alt={selectedPenerimaForRespon.nama}
                  sx={{
                    width: "100px",
                    height: "100px",
                    border: "3px solid",
                    borderColor: "primary.main",
                    mb: 2,
                    boxShadow: 3,
                    fontSize: "2.5rem",
                    bgcolor: "info",
                  }}
                >
                  {selectedPenerimaForRespon.nama
                    ? selectedPenerimaForRespon.nama.charAt(0).toUpperCase()
                    : "👤"}
                </Avatar>
                <MDTypography variant="h6" color="text" fontWeight="medium">
                  {selectedPenerimaForRespon.nama || "Penerima"}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Penerima Instruksi
                </MDTypography>
              </MDBox>

              <MDTypography
                variant="body2"
                color="text"
                mb={2}
                textAlign="center"
              >
                Pilih respon Anda terhadap penyelesaian instruksi ini:
              </MDTypography>

              <MDBox display="flex" flexDirection="column" gap={1.5}>
                <MDButton
                  variant={
                    selectedRespon === "Terima" ? "gradient" : "outlined"
                  }
                  color={selectedRespon === "Terima" ? "success" : "success"}
                  onClick={() => handleSelectRespon("Terima")}
                  startIcon={<Icon>check_circle</Icon>}
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                  }}
                >
                  Terima
                </MDButton>
                <MDButton
                  variant={
                    selectedRespon === "Perbaiki" ? "gradient" : "outlined"
                  }
                  color={selectedRespon === "Perbaiki" ? "warning" : "warning"}
                  onClick={() => {
                    setSelectedRespon("Perbaiki");
                  }}
                  startIcon={<Icon>edit</Icon>}
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                  }}
                >
                  Perbaiki
                </MDButton>
                <MDButton
                  variant={selectedRespon === "Tolak" ? "gradient" : "outlined"}
                  color={selectedRespon === "Tolak" ? "error" : "error"}
                  onClick={() => {
                    setSelectedRespon("Tolak");
                  }}
                  startIcon={<Icon>cancel</Icon>}
                  fullWidth
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                  }}
                >
                  Tolak
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleCloseResponDialog}
          >
            Batal
          </MDButton>
          {/* Tombol Kirim hanya muncul untuk Tolak dan Perbaiki */}
          {selectedRespon !== "Terima" && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleSubmitRespon}
              disabled={!selectedRespon || submittingRespon}
              startIcon={<Icon>send</Icon>}
            >
              {submittingRespon ? "Mengirim..." : "Kirim"}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Rating Bintang untuk Respon Terima */}
      <Dialog
        open={openRatingDialog}
        onClose={() => {}} // Disable close on backdrop click
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <MDTypography variant="h5" color="text" fontWeight="bold">
            {selectedPenerimaForRespon?.respon_pemberi_instruksi === "Terima" &&
            selectedPenerimaForRespon?.nilai
              ? "Edit Penilaian Instruksi"
              : "Penilaian Instruksi"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox textAlign="center" py={2}>
            {/* Tampilkan informasi penerima */}
            {selectedPenerimaForRespon && (
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb={3}
              >
                <Avatar
                  src={
                    selectedPenerimaForRespon.uid_RSS ||
                    selectedPenerimaForRespon.instruksi?.penerima?.uid_RSS
                      ? `https://smartdh.rotendaokab.go.id/uploads/${
                          selectedPenerimaForRespon.uid_RSS ||
                          selectedPenerimaForRespon.instruksi?.penerima?.uid_RSS
                        }.jpg`
                      : undefined
                  }
                  alt={selectedPenerimaForRespon.nama}
                  sx={{
                    width: "100px",
                    height: "100px",
                    border: "3px solid",
                    borderColor: "primary.main",
                    mb: 2,
                    boxShadow: 3,
                    fontSize: "2.5rem",
                    bgcolor: "info",
                  }}
                >
                  {selectedPenerimaForRespon.nama
                    ? selectedPenerimaForRespon.nama.charAt(0).toUpperCase()
                    : "👤"}
                </Avatar>
                <MDTypography variant="h6" color="text" fontWeight="medium">
                  {selectedPenerimaForRespon.nama || "Penerima"}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Penerima Instruksi
                </MDTypography>
              </MDBox>
            )}
            <MDTypography variant="body2" color="text" mb={2}>
              Silakan berikan penilaian Anda terhadap penyelesaian instruksi:
            </MDTypography>
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
              sx={{
                py: 3,
              }}
            >
              {[1, 2, 3, 4, 5].map((rating) => (
                <MDBox
                  key={rating}
                  onClick={() => handleRatingClick(rating)}
                  sx={{
                    cursor: "pointer",
                    width: "75px",
                    height: "75px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": {
                      transform: "scale(1.1)",
                      transition: "transform 0.2s",
                    },
                    transition: "transform 0.2s",
                    "@media (max-width: 600px)": {
                      width: "50px",
                      height: "50px",
                    },
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="60"
                    height="60"
                    fill={selectedRating >= rating ? "#FFD700" : "#E0E0E0"}
                    style={{
                      transition: "fill 0.2s",
                    }}
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </MDBox>
              ))}
            </MDBox>
            {selectedRating > 0 && (
              <MDTypography
                variant="body2"
                color="text.secondary"
                mt={2}
                sx={{ fontStyle: "italic" }}
              >
                Anda memberikan {selectedRating} bintang
                {selectedRating === 1
                  ? " (Sangat Tidak Puas)"
                  : selectedRating === 2
                  ? " (Tidak Puas)"
                  : selectedRating === 3
                  ? " (Cukup)"
                  : selectedRating === 4
                  ? " (Puas)"
                  : " (Sangat Puas)"}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleCancelRating}
          >
            Batal
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleSubmitRating}
            disabled={selectedRating === 0 || submittingRespon}
            startIcon={<Icon>send</Icon>}
          >
            {submittingRespon ? "Mengirim..." : "Kirim"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default DaftarTugas;
